import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../api';
import Loader from '../components/Loader';
import SkillsInput from '../components/SkillsInput';
import DiscoverOpportunities from './DiscoverOpportunities';
import DiscussionsPage from './DiscussionsPage';
import ChatPage from './ChatPage';
import OnboardingTour from '../components/OnboardingTour';
import CardInfoTooltip from '../components/CardInfoTooltip';
import 'react-calendar/dist/Calendar.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  Search, LayoutDashboard, Calendar, MessageSquare, Settings,
  Zap, User, Users, X, CheckCircle, LogOut, FileCheck,
  CreditCard, ExternalLink, Briefcase, Shield, Award, Info, BookOpen, Trash2,
  Lock, ArrowLeft, MoreVertical, Plus, ChevronRight, MapPin, Globe, Mail, Phone,
  Clock, Check, Camera, Download, Upload, AlertCircle, HelpCircle, Star, Eye, EyeOff,
  UserCog, FileText, Inbox, Bell, ShieldCheck, XCircle, Video, Pencil, AlertTriangle, RefreshCw, Copy, Printer
} from 'lucide-react';

import './PopDashboard.css';
import { INDUSTRY_DOMAINS, POP_BACKGROUND_CATEGORIES } from '../constants/domains';
import { EXPERTISE_TAXONOMY } from '../constants/expertise';
import { ALL_LOCATIONS } from '../constants/locations';
import BankDetails from '../components/BankDetails';
import PopLMSSection from '../components/PopLMSSection';

const PopDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [studentRequests, setStudentRequests] = useState([]);
  const [selectedReqPreview, setSelectedReqPreview] = useState(null);
  const [selectedStudentReqPreview, setSelectedStudentReqPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [settingsTab, setSettingsTab] = useState('general');
  const [lmsCourseId, setLmsCourseId] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);

  // My Sessions State
  const [mySessions, setMySessions] = useState([]);
  const [sessionTab, setSessionTab] = useState('upcoming');
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [selectedSessionStudents, setSelectedSessionStudents] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [expertFeedback, setExpertFeedback] = useState([]);
  const [selectedSessionFeedback, setSelectedSessionFeedback] = useState(null);


  // Monthly Calendar Modal State
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [coiAgreed, setCoiAgreed] = useState(false);
  const [selectedDateSessions, setSelectedDateSessions] = useState([]);
  const [selectedDateLabel, setSelectedDateLabel] = useState("");

  // Profile Management Form State
  const [myExpertise, setMyExpertise] = useState([]);
  const [availabilityMode, setAvailabilityMode] = useState([]);
  const [linkedin, setLinkedin] = useState('');
  const [resume, setResume] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [isOtherLocation, setIsOtherLocation] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [highestEducation, setHighestEducation] = useState('');
  const [preferredLocations, setPreferredLocations] = useState([]); // New State
  const [engagementCategories, setEngagementCategories] = useState([]);
  const [backgroundCategory, setBackgroundCategory] = useState('');
  const [expertiseCategory, setExpertiseCategory] = useState('');
  const [expertiseSubcategory, setExpertiseSubcategory] = useState('');
  const [expertiseCore, setExpertiseCore] = useState('');

  // Dynamic Taxonomy Lists
  const [dynIndustries, setDynIndustries] = useState([]);
  const [dynCategories, setDynCategories] = useState([]);
  const [dynSubcategories, setDynSubcategories] = useState([]);
  const [dynCoreExpertise, setDynCoreExpertise] = useState([]);

  useEffect(() => {
    api.get('/expertise-categories').then(res => {
      setDynIndustries(res.data || []);
    }).catch(err => console.error("Could not load dynamic categories:", err));
  }, []);

  useEffect(() => {
    if (!expertiseCategory) {
      setDynCategories([]);
      return;
    }
    const ind = dynIndustries.find(i => i.name === expertiseCategory);
    if (ind) {
      api.get(`/expertise-subcategories?categoryId=${ind.id}`).then(res => {
        setDynCategories(res.data || []);
      }).catch(err => console.error(err));
    } else {
      setDynCategories([]);
    }
  }, [expertiseCategory, dynIndustries]);

  useEffect(() => {
    if (!expertiseSubcategory) {
      setDynSubcategories([]);
      return;
    }
    const cat = dynCategories.find(c => c.name === expertiseSubcategory);
    if (cat) {
      api.get(`/core-expertise?subcategoryId=${cat.id}`).then(res => {
        setDynSubcategories(res.data || []);
      }).catch(err => console.error(err));
    } else {
      setDynSubcategories([]);
    }
  }, [expertiseSubcategory, dynCategories]);

  // Polymorphic Scheduling State
  const [weeklyQuotaHours, setWeeklyQuotaHours] = useState('');
  const [windowStartDate, setWindowStartDate] = useState('');
  const [windowEndDate, setWindowEndDate] = useState('');

  // Pricing Config
  const [individualPrice, setIndividualPrice] = useState(0);
  const [groupSessionPrice, setGroupSessionPrice] = useState(0);
  const [minGroupSize, setMinGroupSize] = useState(1);
  const [universityHourlyRate, setUniversityHourlyRate] = useState(0);

  // Public Session Form State
  const [sessionForm, setSessionForm] = useState({
    title: '', domain: '', event_date: '', start_time: '',
    duration_value: '1', duration_unit: 'Hour', description: '', meeting_link: '', price: 0, isFree: true,
    topics_covered: '', prerequisites: '', materials: { slides: false, documents: false },
    max_participants: 10,
    delivery_mode: 'Remote', location: ''
  });

  // Document Upload State
  const [documentStatus, setDocumentStatus] = useState({
    cv_resume: false, degree_certificates: false, pan_card: false, aadhaar_card: false
  });
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [hasApplied, setHasApplied] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [uploadingCoi, setUploadingCoi] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Preferred Interview Times (Manual Input)
  const [preferredInterview1, setPreferredInterview1] = useState('');
  const [preferredInterview2, setPreferredInterview2] = useState('');
  const [preferredInterview3, setPreferredInterview3] = useState('');

  // Admin Availability Slots for Interview
  const [adminAvailableSlots, setAdminAvailableSlots] = useState([]);
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);

  // Deactivation State
  const [deactivationStatus, setDeactivationStatus] = useState(null);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');

  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);


  // Accept Request Modal State
  const [acceptModal, setAcceptModal] = useState({
    open: false, request: null, form: {
      proposed_amount: 0, meeting_link: '', title: '', event_date: '', start_time: '', duration: ''
    }
  });

  // University Request Modal State
  const [uniAcceptModal, setUniAcceptModal] = useState({
    open: false, request: null, proposed_amount: 0
  });

  // Smart Schedule Modal State
  const [smartModal, setSmartModal] = useState({
    open: false,
    selectedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    startTime: '09:00',
    endTime: '17:00'
  });

  // Change Password State
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Toast notification state
  const [toasts, setToasts] = useState([]);

  // Publish Profile Notification State
  const [showPublishNotification, setShowPublishNotification] = useState(true);

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  };

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null, data: null, onConfirm: null });

  // Prompt modal state
  const [promptModal, setPromptModal] = useState({
    show: false,
    title: '',
    message: '',
    defaultValue: '',
    onSubmit: null
  });

  const handleSmartScheduleSubmit = async () => {
    try {
      if (!smartModal.startTime || !smartModal.endTime || smartModal.selectedDays.length === 0) {
        showToast("Please select days and time range.", 'error');
        return;
      }

      const payload = [];

      smartModal.selectedDays.forEach(day => {
        payload.push({
          day_of_week: day,
          start_time: smartModal.startTime,
          end_time: smartModal.endTime,
          is_available: true
        });
      });

      await api.post('/availability/bulk', payload);
      showToast("Smart Schedule applied successfully!", 'success');
      setSmartModal({ ...smartModal, open: false });
      fetchAvailability();
    } catch (err) {
      showToast("Error applying schedule: " + (err.response?.data?.detail || err.message), 'error');
    }
  };


  const fetchUnreadMessages = useCallback(async () => {
    try {
      const res = await api.get('/chat/conversations');
      const totalUnread = res.data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (err) {
      console.error("Failed to fetch unread messages", err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/profile/me');
      const data = res.data;
      setProfile(data);
      setIsVisible(data.is_visible || false);
      setLinkedin(data.linkedin_url || '');
      setResume(data.resume_link || '');
      setIndividualPrice(data.individual_session_price || 0);
      setGroupSessionPrice(data.group_session_price || 0);
      setMinGroupSize(data.min_group_size || 1);
      setUniversityHourlyRate(data.university_hourly_rate || 0);
      setHighestEducation(data.highest_education || '');
      setMyExpertise(data.skills_list || []);
      setPreferredLocations(data.preferred_locations || []); // Set Preferred Locations

      if (data.availability_mode) setAvailabilityMode(data.availability_mode.split(', '));
      setEngagementCategories(data.engagement_categories || []);
      setBackgroundCategory(data.background_category || '');
      setExpertiseCategory(data.expertise_category || '');
      setExpertiseSubcategory(data.expertise_subcategory || '');
      setExpertiseCore(data.expertise_core || '');

      setWeeklyQuotaHours(data.weekly_quota_hours || '');
      setWindowStartDate(data.window_start_date ? data.window_start_date.split('T')[0] : '');
      setWindowEndDate(data.window_end_date ? data.window_end_date.split('T')[0] : '');


      const reqRes = await api.get('/my-requests');
      setRequests(reqRes.data);

      const stuReqRes = await api.get('/student-requests/me');
      setStudentRequests(stuReqRes.data);

      const earningsRes = await api.get('/my-earnings');
      setTotalEarnings(earningsRes.data.total_earnings || 0);

      const feedbackRes = await api.get('/feedback/expert/me');
      setExpertFeedback(feedbackRes.data.feedbacks || []);

    } catch (err) {
      console.error("Dashboard Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCombinedSessions = useCallback(async () => {
    try {
      const publicRes = await api.get('/my-public-sessions');
      const rawPublicSessions = publicRes.data.map(s => ({
        ...s,
        type: s.university_name ? 'UNIVERSITY' : 'GROUP',  // Identify university sessions
        enrolled_students: s.enrolled_students || [],       // Preserve enrolled students
        participant_count: s.participant_count || 0
      }));

      // Group recurring university sessions
      const groupedSessionsMap = new Map();
      const publicSessions = [];

      rawPublicSessions.forEach(session => {
        if (session.type === 'UNIVERSITY' && session.requirement_id) {
          if (!groupedSessionsMap.has(session.requirement_id)) {
            const group = {
              ...session,
              is_recurring: true,
              session_ids: [session.id],
              dates: [session.event_date]
            };
            groupedSessionsMap.set(session.requirement_id, group);
            publicSessions.push(group);
          } else {
            const group = groupedSessionsMap.get(session.requirement_id);
            group.session_ids.push(session.id);
            group.dates.push(session.event_date);
          }
        } else {
          publicSessions.push(session);
        }
      });

      // Post-process groups to sort dates and create label
      groupedSessionsMap.forEach(group => {
        group.dates.sort();
        group.event_date = group.dates[0];
        group.end_date_recurring = group.dates[group.dates.length - 1];

        // Format date label safely
        const startDate = new Date(group.dates[0].split('T')[0]);
        const endDate = new Date(group.dates[group.dates.length - 1].split('T')[0]);

        const startStr = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        const endStr = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

        group.date_label = `${group.dates.length} Sessions | ${startStr} - ${endStr}`;
      });

      const stuReqRes = await api.get('/student-requests/me');
      const individualSessions = stuReqRes.data
        .filter(r => (r.status === 'ACCEPTED' || r.status === 'COMPLETED') && r.session_type !== 'GROUP')
        .map(r => ({
          id: r.id,
          title: r.title,
          domain: "Mentoring",
          event_date: r.event_date,
          start_time: r.start_time,
          end_time: r.end_time || null,
          duration: r.duration, // Raw string like "1.5 Hours"
          duration_value: r.duration ? parseFloat(r.duration.split(' ')[0]) : null,
          duration_unit: r.duration ? r.duration.split(' ')[1] : null,
          participant_count: 1,
          student_name: r.student_name,
          type: 'INDIVIDUAL',
          payment_status: r.escrow_status,
          amount: r.price || r.proposed_amount || 0, // Ensure amount is captured
          meeting_link: r.meeting_link,
          status: r.status,
          is_completed: r.is_completed,
          stats: { paid: (r.escrow_status === 'HELD' || r.escrow_status === 'RELEASED') ? 1 : 0, total: 1 }
        }));

      const uniReqRes = await api.get('/my-requests');
      const universitySessionsFiltered = uniReqRes.data
        .filter(req => req.status === 'ACCEPTED' || req.status === 'COMPLETED' || req.is_completed)
        .map(req => ({
          id: req.id,
          title: req.requirement_title,
          domain: req.delivery_mode +
            ((['In-Person', 'Hybrid'].includes(req.delivery_mode) && req.location)
              ? ` (${req.location})` : ''),
          event_date: req.event_date,
          start_time: req.start_time,
          end_time: req.end_time || null,
          duration_value: req.duration_value,
          duration_unit: req.duration_unit,
          duration: req.duration_value ? `${req.duration_value} ${req.duration_unit}` : null,
          participant_count: req.enrolled_students?.length || 0,
          student_name: req.university_name,
          type: 'UNIVERSITY',
          payment_status: req.escrow_status,
          is_completed: req.is_completed,
          amount: req.proposed_amount || 0, // Ensure amount is captured
          meeting_link: req.meeting_link,
          enrolled_students: req.enrolled_students,
          requirement_id: req.requirement_id,
          course_id: req.course_id,
          stats: { paid: (req.escrow_status === 'HELD' || req.escrow_status === 'RELEASED') ? 1 : 0, total: 1 }
        }));

      // Filter out university requests that already have generated public sessions
      const scheduledRequirementIds = new Set(
        publicSessions
          .filter(s => s.requirement_id)
          .map(s => s.requirement_id)
      );

      const uniqueUniversityRequests = universitySessionsFiltered.filter(
        req => !req.requirement_id || !scheduledRequirementIds.has(req.requirement_id)
      );

      const finalSessions = [...publicSessions, ...individualSessions, ...uniqueUniversityRequests];
      setMySessions(finalSessions);

      // Bulk fetch feedback
      try {
        const feedbackRes = await api.get('/feedback/expert/me');
        setExpertFeedback(feedbackRes.data.feedbacks || []);
      } catch (err) {
        console.error("Error fetching expert feedback:", err);
      }
    } catch (err) {
      console.error("Error fetching combined sessions:", err);
    }
  }, []);


  const fetchDocumentStatus = useCallback(async () => {
    try {
      const res = await api.get('/document-status');
      setDocumentStatus({
        cv_resume: res.data.cv_resume,
        degree_certificates: res.data.degree_certificates,
        pan_card: res.data.pan_card || false,
        aadhaar_card: res.data.aadhaar_card || false
      });
      setProfileCompletion(res.data.profile_completion_percentage || 0);
      setHasApplied(res.data.has_applied || false);
    } catch (err) {
      console.error("Doc status error:", err);
    }
  }, []);

  const fetchApplicationStatus = useCallback(async () => {
    try {
      const res = await api.get('/vetting/my-application');
      setApplicationStatus(res.data);
    } catch (err) {
      setApplicationStatus(null);
    }
  }, []);

  const fetchDeactivationStatus = useCallback(async () => {
    try {
      const res = await api.get('/accounts/my-deactivation-status');
      setDeactivationStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch deactivation status:', err);
    }
  }, []);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await api.get('/compliance/notifications');
      setNotifications(res.data);
    } catch (e) { }
  }, []);

  const handleMarkAsRead = useCallback(async (id) => {
    try {
      await api.post(`/compliance/notifications/${id}/mark-read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await api.post('/compliance/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) {
      console.error("Failed to mark all notifications as read", e);
    }
  }, []);

  const fetchAdminAvailableSlots = useCallback(async () => {
    try {
      const res = await api.get('/vetting/available-interview-slots');
      setAdminAvailableSlots(res.data || []);
    } catch (err) {
      console.error("Error fetching admin slots:", err);
    }
  }, []);

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await api.get('/availability');

      const formatted = res.data.map(slot => {
        // Check if it's a recurring weekly slot
        if (slot.day_of_week && !slot.specific_date) {
          return {
            id: `recurring-${slot.id}`,
            dbId: slot.id,
            daysOfWeek: [getDayNumber(slot.day_of_week)],
            startTime: slot.start_time,
            endTime: slot.end_time,
            display: 'block',
            backgroundColor: '#4f46e5',
            borderColor: '#4338ca',
            title: 'Weekly Series',
            // START RECURRENCE FROM TODAY - Prevents showing in past weeks
            startRecur: new Date().toISOString().split('T')[0],
            rank: 1
          };
        } else if (slot.specific_date) {
          // Specific date slot - handle the date properly
          let dateOnly = slot.specific_date;

          // If the date contains 'T', extract just the date part
          if (dateOnly.includes('T')) {
            dateOnly = dateOnly.split('T')[0];
          }

          return {
            id: `single-${slot.id}`,
            dbId: slot.id,
            start: `${dateOnly}T${slot.start_time}`,
            end: `${dateOnly}T${slot.end_time}`,
            display: 'block',
            backgroundColor: slot.is_available ? '#10b981' : '#ef4444',
            borderColor: slot.is_available ? '#059669' : '#b91c1c',
            title: slot.is_available ? 'Extra Slot' : 'BLOCKED',
            rank: 2,
            extendedProps: { isBlocked: !slot.is_available }
          };
        }
        return null;
      }).filter(Boolean); // Remove any null entries

      console.log('Formatted slots:', formatted); // Debug log
      setAvailabilitySlots(formatted);
    } catch (err) {
      console.error("Error fetching combined sessions:", err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchAvailability();
    fetchCombinedSessions();
    fetchUnreadMessages();
    fetchDocumentStatus();
    fetchApplicationStatus();
    fetchDeactivationStatus();
    fetchNotifs();
    fetchAdminAvailableSlots();

    // Poll for unread messages every 10 seconds
    const interval = setInterval(fetchUnreadMessages, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchAvailability, fetchCombinedSessions, fetchUnreadMessages, fetchDocumentStatus, fetchApplicationStatus, fetchDeactivationStatus, fetchNotifs, fetchAdminAvailableSlots]);

  useEffect(() => {
    if (location.state?.showSettings || location.state?.showProfile) {
      setActiveTab('profile');
      // Clear state so it doesn't keep switching back on every re-render
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showDiscussions) {
      setActiveTab('discussions');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showChat || location.state?.showMessages) {
      setActiveTab('messages');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (activeTab === 'schedule') fetchAvailability();
    if (activeTab === 'my-sessions') fetchCombinedSessions();
  }, [activeTab, fetchAvailability, fetchCombinedSessions]);

  // Check if professor is approved for scheduling and sessions
    const isApproved = useMemo(() => {
      const isVettingApproved = applicationStatus?.status === 'APPROVED';
      const isCoiSigned = applicationStatus?.coi_status === 'SIGNED';
      const isKycApproved = profile?.kyc_status === 'ACCEPTED';
  
      return isVettingApproved && isCoiSigned && isKycApproved;
    }, [applicationStatus, profile]);

  const handleDateClick = (arg) => {
    const dateStr = arg.dateStr;
    const sessionsOnDay = mySessions.filter(s => {
      if (s.is_recurring && s.dates) {
        return s.dates.some(d => d.split('T')[0] === dateStr);
      }
      return s.event_date.split('T')[0] === dateStr;
    });
    setSelectedDateLabel(new Date(dateStr + "T00:00:00").toLocaleDateString('en-GB', {
      weekday: 'long', month: 'long', day: 'numeric'
    }));
    setSelectedDateSessions(sessionsOnDay);
  };

  const canSubmitForVetting = useMemo(() => {
    const hasAllDocs = documentStatus.cv_resume && documentStatus.degree_certificates;
    // KYC documents (PAN/Aadhaar) are now optional for initial vetting submission but recommended
    // Allow submission if no application, if declined, or if approved but not scheduled/completed yet
    const canApply = !applicationStatus?.has_application ||
      applicationStatus?.status === 'DECLINED' ||
      (applicationStatus?.status === 'APPROVED' && (!applicationStatus?.interview_status || applicationStatus?.interview_status === 'PENDING'));
    // Schedule is now optional - not required for vetting submission
    return hasAllDocs && profile.full_name && profile.job_title && profile.company &&
      profile.domain && canApply;
  }, [documentStatus, profile, applicationStatus]);

  // Check if professor is approved for scheduling and sessions
  // const isApproved = useMemo(() => {
  //   return applicationStatus?.status === 'APPROVED';
  // }, [applicationStatus]);

  const calendarEvents = useMemo(() => {
    return mySessions.flatMap(s => {
      let bgColor = '#4f46e5';
      if (s.type === 'INDIVIDUAL') bgColor = '#10b981';
      if (s.type === 'UNIVERSITY') bgColor = '#f59e0b';

      if (s.is_recurring && s.dates) {
        return s.dates.map(date => ({
          title: s.title,
          start: date.split('T')[0],
          display: 'list-item',
          backgroundColor: bgColor
        }));
      }

      return [{
        title: s.title,
        start: s.event_date.split('T')[0],
        display: 'list-item',
        backgroundColor: bgColor
      }];
    });
  }, [mySessions]);

  // const handleDateClick = (arg) => {
  //   const dateStr = arg.dateStr;
  //   const sessionsOnDay = mySessions.filter(s => s.event_date.split('T')[0] === dateStr);
  //   setSelectedDateLabel(new Date(dateStr).toLocaleDateString('en-US', {
  //     weekday: 'long', month: 'long', day: 'numeric'
  //   }));
  //   setSelectedDateSessions(sessionsOnDay);
  // };

  const getDayNumber = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days.indexOf(day);
  };

  const getDayName = (num) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[num];
  };


  const handleCalendarSelect = async (selectInfo) => {
    const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(selectInfo.start);
    const startTime = selectInfo.startStr.split('T')[1]?.substring(0, 5) || '09:00';
    const endTime = selectInfo.endStr.split('T')[1]?.substring(0, 5) || '10:00';

    setConfirmModal({
      show: true,
      type: 'recurring_slot',
      data: { dayName, startTime, endTime, selectInfo },
      onConfirm: async (isRecurring) => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        try {
          const payload = {
            start_time: startTime,
            end_time: endTime,
            is_available: true
          };

          if (isRecurring) {
            // Weekly recurring
            payload.day_of_week = dayName;
            payload.specific_date = null;
          } else {
            // Specific date
            payload.day_of_week = null;
            payload.specific_date = selectInfo.startStr.split('T')[0];
          }

          await api.post('/availability', payload);
          await fetchAvailability();
        } catch (err) {
          console.error('Error saving slot:', err);
          showToast("Error saving slot: " + (err.response?.data?.detail || err.message), 'error');
        }
      }
    });
  };

  const handleSlotDelete = async (clickInfo) => {
    const eventId = clickInfo.event.id;
    const dbId = eventId.split('-')[1];

    if (eventId.startsWith('recurring')) {
      setConfirmModal({
        show: true,
        type: 'recurring_delete',
        data: { dbId, clickInfo },
        onConfirm: async (choice) => {
          setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
          if (choice === 'ALL') {
            try {
              await api.delete(`/availability/${dbId}`);
              await fetchAvailability();
            } catch (e) {
              showToast("Delete failed.", 'error');
            }
          } else if (choice === 'ONLY') {
            try {
              const specificDate = clickInfo.event.start.toISOString().split('T')[0];
              const startTime = clickInfo.event.start.toTimeString().substring(0, 5);
              const endTime = clickInfo.event.end ? clickInfo.event.end.toTimeString().substring(0, 5) : '10:00';

              await api.post('/availability', {
                specific_date: specificDate,
                day_of_week: null,
                is_available: false,
                start_time: startTime,
                end_time: endTime
              });
              await fetchAvailability();
            } catch (e) {
              console.error('Block error:', e);
              showToast("Could not block date.", 'error');
            }
          }
        }
      });
    } else {
      setConfirmModal({
        show: true,
        type: 'simple_confirm',
        data: { message: "Remove this specific adjustment?", dbId },
        onConfirm: async () => {
          setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
          try {
            await api.delete(`/availability/${dbId}`);
            await fetchAvailability();
          } catch (e) {
            showToast("Delete failed.", 'error');
          }
        }
      });
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Only PDF files allowed', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('Max file size is 10MB', 'error');
      return;
    }
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/upload-document/${docType}`, formData);

      showToast(`${docType.replace(/_/g, ' ')} uploaded successfully!`, 'success');
      await fetchDocumentStatus();
    } catch (err) {
      console.error(err);
      showToast('Upload failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setUploadingDoc(null);
      // Removed response.json processing since axios handles it and throws on error
    }
  };

  const handleDocumentDelete = async (docType) => {
    setConfirmModal({
      show: true,
      type: 'simple_confirm',
      data: { message: `Delete ${docType.replace(/_/g, ' ')}?` },
      onConfirm: async () => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        try {
          await api.delete(`/delete-document/${docType}`);
          await fetchDocumentStatus();
        } catch (err) {
          showToast('Delete failed', 'error');
        }
      }
    });
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Only JPEG, PNG and WEBP images allowed', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Max image size is 5MB', 'error');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload-photo', formData);
      showToast('Profile photo updated!', 'success');
      setProfile(prev => ({ ...prev, profile_photo_path: response.data.profile_photo_path }));
    } catch (err) {
      console.error(err);
      showToast('Upload failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    setConfirmModal({
      show: true,
      type: 'simple_confirm',
      data: { message: 'Remove profile photo?' },
      onConfirm: async () => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        try {
          await api.delete('/delete-photo');
          showToast('Photo removed', 'success');
          setProfile(prev => ({ ...prev, profile_photo_path: null }));
        } catch (err) {
          showToast('Delete failed', 'error');
        }
      }
    });
  };

  const handleSubmitForVetting = async () => {
    if (!canSubmitForVetting) {
      showToast('Complete all requirements first.', 'error');
      return;
    }
    const scheduleNote = availabilitySlots.length > 0
      ? `\n- ${availabilitySlots.length} schedule slots`
      : '\n- No schedule set (can be added later)';

    setConfirmModal({
      show: true,
      type: 'vetting_submit',
      data: {
        message: `Submit for vetting?\n\n- Profile info\n- ${Object.values(documentStatus).filter(Boolean).length} documents${scheduleNote}`,
        scheduleNote
      },
      onConfirm: async () => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        setSubmittingApplication(true);
        try {
          await handleSaveProfile(false);
          await api.post('/vetting/submit-application', {
            profile_data: {
              full_name: profile.full_name,
              job_title: profile.job_title,
              company: profile.company,
              domain: profile.domain,
              years_of_experience: profile.years_of_experience,
              highest_education: highestEducation,
              location: profile.location,
              expertise_areas: myExpertise.join(', '),
              availability_mode: availabilityMode.join(', '),
              linkedin_url: linkedin,
              resume_link: resume
            },
            availability_slots: availabilitySlots.map(s => ({
              type: s.daysOfWeek ? 'weekly' : 'specific',
              day_of_week: s.daysOfWeek ? getDayName(s.daysOfWeek[0]) : null,
              specific_date: s.start ? s.start.split('T')[0] : null,
              start_time: s.startTime || (s.start ? s.start.split('T')[1]?.substring(0, 5) : null),
              end_time: s.endTime || (s.end ? s.end.split('T')[1]?.substring(0, 5) : null)
            })),
            documents_uploaded: documentStatus,
            preferred_interview_time_1: preferredInterview1 || null,
            preferred_interview_time_2: preferredInterview2 || null,
            preferred_interview_time_3: preferredInterview3 || null,
            preferred_slot_ids: selectedSlotIds
          });
          showToast('Application submitted!', 'success');
          setHasApplied(true);
          await fetchDocumentStatus();
          await fetchApplicationStatus();
        } catch (err) {
          console.error("Full submission error:", err);
          console.error("Response data:", JSON.stringify(err.response?.data));
          const detail = err.response?.data?.detail;
          let errorMsg;
          if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            // Pydantic 422 validation errors
            errorMsg = detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join('; ');
          } else if (detail && typeof detail === 'object') {
            errorMsg = JSON.stringify(detail);
          } else {
            errorMsg = err.response?.data?.message || `HTTP ${err.response?.status || 'Error'}: ${err.message}`;
          }
          showToast('Submission failed: ' + errorMsg, 'error');
        } finally {
          setSubmittingApplication(false);
        }
      }
    });
  };

  const handleRequestDeactivation = async () => {
    if (!deactivationReason.trim()) {
      showToast('Please provide a reason for deactivation', 'error');
      return;
    }
    try {
      await api.post('/accounts/request-deactivation', { reason: deactivationReason });
      showToast('Deactivation request submitted successfully', 'success');
      setShowDeactivationModal(false);
      setDeactivationReason('');
      await fetchDeactivationStatus();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to submit request', 'error');
    }
  };

  const handleCancelDeactivation = async () => {
    setConfirmModal({
      show: true,
      type: 'simple_confirm',
      data: { message: 'Are you sure you want to cancel your deactivation request?' },
      onConfirm: async () => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        try {
          await api.delete('/accounts/cancel-deactivation-request');
          showToast('Deactivation request cancelled', 'success');
          setDeactivationStatus({ has_request: false });
        } catch (err) {
          showToast(err.response?.data?.detail || 'Failed to cancel request', 'error');
        }
      }
    });
  };

  const handleCreatePublicSession = async (e) => {
    e.preventDefault();

    const payload = {
      ...sessionForm,
      materials_slides: sessionForm.materials.slides,
      materials_documents: sessionForm.materials.documents
    };
    if (payload.isFree) {
      payload.price = 0;
    }
    delete payload.isFree;
    // Remove the materials object since we're sending separate fields
    delete payload.materials;

    try {
      if (editingSession) {
        await api.put(`/public-sessions/${editingSession}`, payload);
        showToast("Session updated!", 'success');
      } else {
        await api.post('/public-sessions', payload);
        showToast("Session published!", 'success');
      }
      setEditingSession(null);
      setSessionForm({ title: '', domain: INDUSTRY_DOMAINS[0], event_date: '', start_time: '', duration_value: '1', duration_unit: 'Hour', description: '', meeting_link: '', price: 0, isFree: true, topics_covered: '', prerequisites: '', materials: { slides: false, documents: false }, max_participants: 50 });
      fetchCombinedSessions();
      setActiveTab('my-sessions');
      setActiveTab('my-sessions');
    } catch (err) {
      showToast(err.response?.data?.detail || "Error saving session.", 'error');
    }
  };

  const handleRequestAction = async (requestId, newStatus) => {
    if (newStatus === 'Accepted') {
      // Open university request modal instead of prompt
      const req = requests.find(r => r.id === requestId);

      // Calculate price based on hourly rate and duration
      // Fallback calculation only if total_estimated_hours is missing and it's not ADJUNCT (which usually has weekly_hours)
      if (!req.total_estimated_hours) {
        // Validation/Calculation logic if needed in future
      }

      const calculatedAmount = req.total_estimated_amount || req.budget || 0;

      setUniAcceptModal({
        open: true,
        request: req,
        proposed_amount: calculatedAmount
      });
      return;
    }

    setConfirmModal({
      show: true,
      type: 'simple_confirm',
      data: { message: `Confirm ${newStatus.toLowerCase()}?` },
      onConfirm: async () => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        setProcessing(requestId);
        try {
          await api.put(`/engage/${requestId}/respond?status=${newStatus.toUpperCase()}`);
          setRequests(prev => prev.map(req =>
            req.id === requestId ? { ...req, status: newStatus.toUpperCase() } : req
          ));
        } catch (err) { showToast("Failed to update status.", 'error'); }
        finally { setProcessing(null); }
      }
    });
  };

  const submitUniversityAcceptance = async () => {
    const { request, proposed_amount } = uniAcceptModal;
    setProcessing(request.id);
    try {
      const url = `/engage/${request.id}/respond?status=ACCEPTED&proposed_amount=${proposed_amount}`;
      await api.put(url);
      setRequests(prev => prev.map(req =>
        req.id === request.id ? { ...req, status: 'ACCEPTED' } : req
      ));
      setUniAcceptModal({ open: false, request: null, proposed_amount: 0 });
      showToast("University Engagement Accepted!", 'success');
    } catch (err) {
      showToast("Failed to accept: " + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setProcessing(null);
    }
  };

  const downloadCoiDisclaimer = (action = 'download') => {
    const printWindow = window.open('', '_blank');
    const content = `
      <html>
        <head>
          <title>CoI Declaration - Professor of Practice</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; max-width: 800px; margin: 0 auto; }
            h1 { color: var(--proto-white); border-bottom: 2px solid var(--border); padding-bottom: 10px; font-size: 24px; }
            h2 { color: var(--proto-white); font-size: 18px; margin-top: 25px; }
            .declaration-text { margin-top: 30px; font-weight: bold; border: 1px solid var(--border); padding: 20px; background: #f8fafc; border-left: 4px solid #6366f1; }
            .signature-box { margin-top: 50px; border-top: 1px solid #000; width: 300px; padding-top: 10px; }
            .no-print { margin-top: 40px; display: flex; gap: 12px; }
            button { padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: opacity 0.2s; }
            .btn-primary { background: #6366f1; color: white; }
            .btn-secondary { background: var(--border); color: var(--text-muted); }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body onload="${action === 'print' ? 'setTimeout(() => window.print(), 500)' : ''}">
          <h1>Professor of Practice (PoP) – Conflict of Interest & Employment Disclosure Disclaimer</h1>
          <p>The Professor of Practice (PoP) appointment is designed to bring industry expertise into academia while maintaining the highest standards of academic integrity, transparency, and institutional governance.</p>
          <p>By accepting or applying for a PoP role, the expert agrees to the following:</p>

          <h2>1. Primary Employment Acknowledgment</h2>
          <p>The PoP engagement is typically a <b>part-time / adjunct / practice-based academic role</b>. The expert confirms that their primary employment, business interests, or professional engagements remain outside the institution unless otherwise formally agreed in writing.</p>

          <h2>2. Conflict of Interest Disclosure</h2>
          <p>The PoP shall fully disclose any <b>current employment, consulting assignments, advisory roles, directorships, or business relationships</b> that may create an actual, potential, or perceived conflict of interest with: The institution, Students, Academic programs, Research, projects, or evaluation activities.</p>

          <h2>3. No Undue Influence</h2>
          <p>The PoP agrees not to use their academic position to promote specific companies, products, or services for personal or organizational gain; influence student decisions for recruitment, sales, or commercial advantage; or direct institutional resources toward personal business interests.</p>

          <h2>4. Separation of Roles</h2>
          <p>The PoP role is academic and mentoring in nature, and does not constitute an employment relationship with decision-making authority or authority to represent the institution in commercial agreements.</p>

          <h2>5. Transparency with Students</h2>
          <p>Where relevant, the PoP shall disclose their industry affiliation to students, especially when supervising projects, internships, case studies, or industry-linked work.</p>

          <h2>6. Use of Institutional Position</h2>
          <p>The PoP shall not use the institution's name, brand, or affiliation for personal commercial purposes, marketing, or fundraising without prior written approval.</p>

          <h2>7. Ongoing Obligation</h2>
          <p>The duty to disclose conflicts of interest is <b>continuous</b>. Any change in professional status, employment, or financial interest must be reported promptly.</p>

          <h2>8. Institutional Review</h2>
          <p>The institution reserves the right to review disclosed interests and determine mitigation measures or whether the engagement should be modified/discontinued.</p>

          <div class="declaration-text">
            <b>Declaration:</b> "I confirm that I have read, understood, and disclosed all relevant professional engagements and potential conflicts of interest. I agree to abide by the academic, ethical, and governance standards applicable to the Professor of Practice role."
          </div>

          <div class="signature-box">
            Signature of PoP
            <br/><br/>
            Name: ${profile.full_name || '________________'}
            <br/>
            Date: ${new Date().toLocaleDateString()}
          </div>
          
          <div class="no-print">
            <button onclick="window.print()" class="btn-primary">${action === 'print' ? 'Print Disclaimer' : 'Save as PDF'}</button>
            <button onclick="window.close()" class="btn-secondary">Close Window</button>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const handleSignCoi = async () => {
    if (!coiAgreed) {
      showToast("Please agree to the Conflict of Interest terms.", 'error');
      return;
    }

    setUploadingCoi(true);
    
    try {
      await api.post('/vetting/sign-coi', { agreed: true });
      showToast("COI digitally signed and profile activated!", 'success');
      fetchApplicationStatus();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to sign COI declaration.", 'error');
    } finally {
      setUploadingCoi(false);
    }
  };

  const handleStudentRequestAction = async (requestId, newStatus) => {
    if (newStatus === 'Accepted') {
      const req = studentRequests.find(r => r.id === requestId);
      setAcceptModal({
        open: true,
        request: req,
        form: {
          proposed_amount: (req.price && req.price > 0) ? req.price : (req.session_type === 'GROUP' ? (groupSessionPrice || individualPrice || 0) : (individualPrice || 0)),
          meeting_link: '',
          title: req.title,
          event_date: req.event_date.split('T')[0],
          start_time: req.start_time,
          duration: req.duration || '1 Hour',
          description: req.description || ''
        }
      });
      return;
    }

    setConfirmModal({
      show: true,
      type: 'student_request',
      data: { requestId, newStatus },
      onConfirm: () => {
        handleStudentRequestAction(requestId, newStatus);
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
      }
    });
    setProcessing(requestId);
    try {
      await api.put(`/student-requests/${requestId}/respond?status=${newStatus.toUpperCase()}`);
      setStudentRequests(prev => prev.map(req => req.id === requestId ? { ...req, status: newStatus.toUpperCase() } : req));
    } catch (err) { showToast("Failed to update student request.", 'error'); }
    finally { setProcessing(null); }
  };

  const submitAcceptance = async () => {
    const { request, form } = acceptModal;
    setProcessing(request.id);
    try {
      if (!form.meeting_link) {
        showToast("Please enter a meeting link before accepting.", 'error');
        setProcessing(null);
        return;
      }

      // Ensure we have valid integers for price 
      // Use the FIXED request price as the source of truth, fallback to form if needed
      const priceSource = (request.price && request.price > 0) ? request.price : form.proposed_amount;
      const finalPrice = parseInt(priceSource) || 0;

      const payload = {
        status: "ACCEPTED",
        proposed_amount: finalPrice,
        meeting_link: form.meeting_link,
        title: form.title || request.title,
        description: form.description || request.description,
        event_date: form.event_date || request.event_date.split('T')[0],
        start_time: form.start_time || request.start_time,
        duration: request.duration || form.duration || "1 Hour" // Prioritize request duration logic
      };

      console.log("Submitting acceptance payload:", payload); // Debug log

      await api.put(`/student-requests/${request.id}/respond`, payload);

      setStudentRequests(prev => prev.map(req => req.id === request.id ? { ...req, status: "ACCEPTED" } : req));
      fetchCombinedSessions();
      fetchAvailability();
      setAcceptModal({ open: false, request: null, form: {} });
      showToast("Session Accepted & Scheduled!", 'success');
    } catch (err) {
      showToast("Failed to accept: " + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setProcessing(null);
    }
  };

  const toggleSelection = (list, setList, value) => {
    if (list.includes(value)) setList(list.filter(item => item !== value));
    else setList([...list, value]);
  };

  const handleSaveProfile = async (makeVisible) => {
    // Validation: Min 1 Location if On-Site modes selected
    const isOnSite = availabilityMode.includes('In-Person') || availabilityMode.includes('Hybrid');
    if (isOnSite && preferredLocations.length === 0) {
      showToast("Validation Error: You have selected 'In-Person' or 'Hybrid' delivery modes. Please add at least 1 preferred on-site location.", 'error');
      setSettingsTab('preferences'); // Switch to preferences tab to show error context
      return;
    }

    if (profile.job_title && !/^[A-Za-z\s\.\-\&]+$/.test(profile.job_title.trim())) {
      showToast("Job title can only contain letters and valid characters.", 'error');
      return;
    }
    const yoe = parseInt(profile.years_of_experience);
    if (isNaN(yoe) || yoe < 0) {
      showToast("Years of experience must be 0 or greater.", 'error');
      return;
    }
    if (!linkedin || !linkedin.trim()) {
      showToast("LinkedIn profile URL is required.", 'error');
      return;
    } else if (!/^https?:\/\/(www\.)?linkedin\.com\/.*$/.test(linkedin.trim())) {
      showToast("Please enter a valid LinkedIn profile URL.", 'error');
      return;
    }
    if (resumeFile) {
        const ext = resumeFile.name.toLowerCase().split('.').pop();
        if (!['pdf', 'doc', 'docx'].includes(ext)) {
            showToast("Only PDF, DOC, and DOCX formats are supported.", 'error');
            return;
        } else if (resumeFile.size > 5 * 1024 * 1024) {
            showToast("Resume file size must be less than 5MB.", 'error');
            return;
        }
    }

    try {
      if (resumeFile) {
        const formData = new FormData();
        formData.append("file", resumeFile);
        await api.post("/upload-document/cv_resume", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      const payload = {
        phone_number: profile.phone_number,
        full_name: profile.full_name,
        job_title: profile.job_title,
        company: profile.company,
        domain: profile.domain,
        years_of_experience: parseInt(profile.years_of_experience) || 0,
        highest_education: highestEducation,
        location: `${profile.city || ''}, ${profile.state || ''}, ${profile.country || ''}`,
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || '',
        bio: profile.bio,
        is_visible: makeVisible,
        availability_mode: availabilityMode.join(', '),
        linkedin_url: linkedin,
        resume_link: resume,
        skills_list: myExpertise,
        individual_session_price: individualPrice,
        group_session_price: groupSessionPrice,
        min_group_size: minGroupSize,
        university_hourly_rate: universityHourlyRate,
        preferred_locations: preferredLocations, // Save Preferred Locations
        engagement_categories: engagementCategories,
        background_category: backgroundCategory,
        expertise_category: expertiseCategory,
        expertise_subcategory: expertiseSubcategory,
        expertise_core: expertiseCore,
        weekly_quota_hours: parseInt(weeklyQuotaHours) || null,
        window_start_date: windowStartDate || null,
        window_end_date: windowEndDate || null
      };
      await api.put('/profile/pop', payload);
      setIsVisible(makeVisible);
      showToast(makeVisible ? "Profile is Live!" : "Draft Saved.", 'success');
      fetchData();
    } catch (err) {
      showToast("Error saving settings.", 'error');
      throw err;
    }
  };

  const handleChangePassword = async () => {
    // Validate inputs
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showToast('Please fill in all password fields', 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showToast('New password must be at least 6 characters long', 'error');
      return;
    }

    try {
      await api.post('/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      showToast('Password changed successfully!', 'success');
      setShowChangePasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to change password', 'error');
    }
  };

  const stats = useMemo(() => ({
    total: requests.length + studentRequests.length,
    pending: requests.filter(r => ['requested', 'pending'].includes(r.status.toLowerCase())).length +
      studentRequests.filter(r => r.status.toLowerCase() === 'pending').length,
    accepted: requests.filter(r => r.status.toLowerCase() === 'accepted').length +
      studentRequests.filter(r => r.status.toLowerCase() === 'accepted').length,
    earnings: totalEarnings
  }), [requests, studentRequests, totalEarnings]);

  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    // Avoid timezone shift by splitting off the time part
    return new Date(dateString.split('T')[0]).toLocaleDateString('en-GB');
  };

  const handleDeleteSession = async (sessionId, type) => {
    if (type === 'INDIVIDUAL') {
      showToast("Individual mentoring sessions must be cancelled via the Overview tab.", 'error');
      return;
    }
    setConfirmModal({
      show: true,
      type: 'simple_confirm',
      data: { message: "Delete this masterclass?" },
      onConfirm: async () => {
        setConfirmModal({ show: false, type: null, data: null, onConfirm: null });
        try {
          await api.delete(`/public-sessions/${sessionId}`);
          fetchCombinedSessions();
        } catch (err) { showToast("Failed to delete.", 'error'); }
      }
    });
  };

  const handleEditClick = (session) => {
    if (session.type === 'INDIVIDUAL') {
      showToast("Individual mentoring details cannot be edited manually.", 'error');
      return;
    }
    if (session.requirement_id) {
      showToast("University-managed sessions cannot be edited manually. Please contact the institution for changes.", 'error');
      return;
    }
    const durationParts = session.duration ? session.duration.split(' ') : ['1', 'Hour'];
    setSessionForm({
      title: session.title,
      domain: session.domain,
      event_date: session.event_date.split('T')[0],
      start_time: session.start_time,
      duration_value: durationParts[0] || '1',
      duration_unit: durationParts[1] || 'Hour',
      description: session.description || '',
      meeting_link: session.meeting_link || '',
      price: session.price || 0,
      isFree: !(session.price > 0),
      topics_covered: session.topics_covered || '',
      prerequisites: session.prerequisites || '',
      materials: {
        slides: session.materials_slides || false,
        documents: session.materials_documents || false
      },
      max_participants: session.max_participants || 50,
      delivery_mode: session.delivery_mode || 'Remote',
      location: session.location || ''
    });
    setEditingSession(session.id);
    setActiveTab('create-session');
  };

  if (loading) return <Loader />;

  const renderMonthlyModal = () => (
    isCalendarModalOpen && (
      <div className="pop-modal-overlay" onClick={() => setIsCalendarModalOpen(false)}>
        <div className="pop-calendar-modal pop-fade-up" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Monthly Engagement View</h3>
            <button className="pop-close-btn" onClick={() => setIsCalendarModalOpen(false)}><XCircle size={22} /></button>
          </div>
          <div className="modal-split-body">
            <div className="modal-cal-section">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={calendarEvents}
                dateClick={handleDateClick}
                height="450px"
                headerToolbar={{ left: 'prev,next', center: 'title', right: 'today' }}
              />
            </div>
            <div className="modal-agenda-section">
              <h4>{selectedDateLabel || "Select a date"}</h4>
              <div className="agenda-scroll">
                {selectedDateSessions.length > 0 ? (
                  selectedDateSessions.map((s, idx) => (
                    <div key={idx} className={`agenda-card ${s.type.toLowerCase()}`}>
                      <div className="agenda-time">
                        <Clock size={14} />
                        {s.start_time}{s.end_time ? ` - ${s.end_time}` : ''}
                      </div>
                      <div className="agenda-title">{s.title}</div>
                      <div className="agenda-meta">{s.type} • {s.student_name || s.domain}</div>
                    </div>
                  ))
                ) : <p className="empty-msg">No sessions on this day.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );

  const renderScheduleTab = () => {
    if (!isApproved) {
      return (
        <div className="pop-fade-up">
          <div className="pop-glass-card">
            <div className="card-header">
              <h3>⚠️ Scheduling Not Available</h3>
            </div>
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <AlertTriangle size={64} color="#f59e0b" style={{ marginBottom: '20px' }} />
              <h2 style={{ marginBottom: '10px', color: '#1f2937' }}>Vetting Approval Required</h2>
              <p style={{ color: '#6b7280', marginBottom: '20px', maxWidth: '500px', margin: '0 auto' }}>
                You need to be approved by the admin through the vetting process before you can set your availability schedule.
                {applicationStatus?.status === 'PENDING' && ' Your application is currently under review.'}
                {applicationStatus?.status === 'UNDER_REVIEW' && ' Your application is being reviewed by our team.'}
                {!hasApplied && ' Please complete your profile and submit for vetting in the Settings tab.'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="pop-fade-up">
        <div className="pop-glass-card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3>Smart Availability Schedule</h3>
              <p className="pop-header-sub"><b>Indigo:</b> Weekly | <b>Green:</b> One-time | <b>Red:</b> Blocked Exceptions</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="pop-btn-secondary"
                onClick={() => setIsCalendarModalOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}
              >
                <Calendar size={16} /> Monthly View
              </button>
              <button className="pop-btn-primary" onClick={() => setSmartModal({ ...smartModal, open: true })}>
                <Zap size={16} style={{ marginRight: '6px' }} /> Smart Schedule
              </button>
            </div>
          </div>
          <div className="schedule-stats">
            <div className="schedule-stat">
              <span className="stat-number">{availabilitySlots.filter(s => s.daysOfWeek).length}</span>
              <span className="stat-label">Weekly</span>
            </div>
            <div className="schedule-stat">
              <span className="stat-number">{availabilitySlots.filter(s => !s.daysOfWeek).length}</span>
              <span className="stat-label">Specific</span>
            </div>
            <div className="schedule-stat">
              <span className="stat-number">{availabilitySlots.length}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
          <div className="pop-calendar-container smaller-calendar">
            <FullCalendar
              plugins={[timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              allDaySlot={false}
              selectable={true}
              selectAllow={(selectInfo) => {
                return selectInfo.start >= new Date();
              }}
              events={availabilitySlots}
              eventOrder="-rank"
              select={handleCalendarSelect}
              eventClick={handleSlotDelete}
              height="550px"
              slotMinTime="08:00:00"
              slotMaxTime="21:00:00"
              slotDuration="00:30:00"
              headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
              dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderCreateSessionTab = () => {
    if (!isApproved) {
      return (
        <div className="pop-fade-up">
          <div className="pop-glass-card">
            <div className="card-header">
              <h3>⚠️ Session Creation Not Available</h3>
            </div>
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <AlertTriangle size={64} color="#f59e0b" style={{ marginBottom: '20px' }} />
              <h2 style={{ marginBottom: '10px', color: '#1f2937' }}>Vetting Approval Required</h2>
              <p style={{ color: '#6b7280', marginBottom: '20px', maxWidth: '500px', margin: '0 auto' }}>
                You need to be approved by the admin through the vetting process before you can host public sessions.
                {applicationStatus?.status === 'PENDING' && ' Your application is currently under review.'}
                {applicationStatus?.status === 'UNDER_REVIEW' && ' Your application is being reviewed by our team.'}
                {!hasApplied && ' Please complete your profile and submit for vetting in the Settings tab.'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="pop-fade-up">
        <div className="pop-glass-card">
          <div className="card-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>{editingSession ? '✏️ Edit Session' : 'Host a Public Session'}</h3>
              {editingSession && (
                <button className="pop-btn-outline sm" onClick={() => {
                  setEditingSession(null);
                  setSessionForm({ title: '', domain: INDUSTRY_DOMAINS[0], event_date: '', start_time: '', duration_value: '1', duration_unit: 'Hour', description: '', meeting_link: '', price: 0, isFree: true, topics_covered: '', prerequisites: '', materials: { slides: false, documents: false }, max_participants: 50 });
                }}>Cancel Edit</button>
              )}
            </div>
          </div>
          <form onSubmit={handleCreatePublicSession} className="pop-profile-form-vertical" style={{ marginTop: '20px' }}>
            <div className="pop-field-wrapper">
              <label>Session Title: </label>
              <input required value={sessionForm.title} onChange={e => setSessionForm({ ...sessionForm, title: e.target.value })} placeholder="Session Title" />
            </div>
            {/* Mode & Domain Selection */}
            <div className="pop-grid-2">
              <div className="pop-field-wrapper">
                <label>Delivery Mode: </label>
                <select
                  value={sessionForm.delivery_mode}
                  onChange={e => setSessionForm({ ...sessionForm, delivery_mode: e.target.value })}
                  required
                >
                  <option value="Remote">Remote (Online)</option>
                  <option value="In-Person">In-Person</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>
              <div className="pop-field-wrapper">
                <label>Domain: </label>
                <select
                  value={sessionForm.domain}
                  onChange={e => setSessionForm({ ...sessionForm, domain: e.target.value })}
                  required
                >
                  <option value="">Select Domain</option>
                  {INDUSTRY_DOMAINS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>


            {/* Location or Meeting Link */}
            {sessionForm.delivery_mode === 'Hybrid' ? (
              <>
                <div className="pop-field-wrapper">
                  <label>Location (City): </label>
                  <input
                    required
                    value={sessionForm.location}
                    onChange={e => setSessionForm({ ...sessionForm, location: e.target.value })}
                    placeholder="e.g. Mumbai (Must match your profile preferences)"
                  />
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Must be one of your preferred locations: {preferredLocations.join(', ') || 'None set'}
                  </p>
                </div>
                <div className="pop-field-wrapper">
                  <label>Meeting Link (for remote participants): </label>
                  <input
                    required
                    value={sessionForm.meeting_link}
                    onChange={e => setSessionForm({ ...sessionForm, meeting_link: e.target.value })}
                    placeholder="https://zoom.us/j/..."
                  />
                </div>
              </>
            ) : sessionForm.delivery_mode === 'In-Person' ? (
              <div className="pop-field-wrapper">
                <label>Location (City): </label>
                <input
                  required
                  value={sessionForm.location}
                  onChange={e => setSessionForm({ ...sessionForm, location: e.target.value })}
                  placeholder="e.g. Mumbai (Must match your profile preferences)"
                />
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Must be one of your preferred locations: {preferredLocations.join(', ') || 'None set'}
                </p>
              </div>
            ) : (
              <div className="pop-field-wrapper">
                <label>Meeting Link (Mandatory): </label>
                <input
                  required
                  value={sessionForm.meeting_link}
                  onChange={e => setSessionForm({ ...sessionForm, meeting_link: e.target.value })}
                  placeholder="https://zoom.us/j/..."
                />
              </div>
            )}

            <div className="pop-grid-2" style={{ marginTop: '10px' }}>
              <div className="pop-field-wrapper">
                <label>Date: </label>
                <input type="date" required value={sessionForm.event_date} onChange={e => setSessionForm({ ...sessionForm, event_date: e.target.value })} />
              </div>
            </div>
            <div className="pop-grid-2">
              <div className="pop-field-wrapper">
                <label>Start Time: </label>
                <input type="time" required value={sessionForm.start_time} onChange={e => setSessionForm({ ...sessionForm, start_time: e.target.value })} />
              </div>
              <div className="pop-field-wrapper">
                <label>Duration: </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    min="1"
                    style={{ flex: 1 }}
                    value={sessionForm.duration_value}
                    onChange={e => setSessionForm({ ...sessionForm, duration_value: e.target.value })}
                    required
                  />
                  <select
                    style={{ flex: 2 }}
                    value={sessionForm.duration_unit}
                    onChange={e => setSessionForm({ ...sessionForm, duration_unit: e.target.value })}
                  >
                    <option value="Minutes">Minutes</option>
                    <option value="Hour">Hour(s)</option>
                    <option value="Day">Day(s)</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="pop-grid-2">
              <div className="pop-field-wrapper">
                <label>Pricing Type: </label>
                <select
                  value={sessionForm.isFree ? "Free" : "Paid"}
                  onChange={e => {
                    if (e.target.value === "Free") {
                      setSessionForm({ ...sessionForm, isFree: true, price: 0 });
                    } else {
                      setSessionForm({ ...sessionForm, isFree: false, price: 500 });
                    }
                  }}
                >
                  <option value="Free">Free</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="pop-field-wrapper">
                <label>Maximum Participants: </label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={sessionForm.max_participants}
                  onChange={e => setSessionForm({ ...sessionForm, max_participants: parseInt(e.target.value) || 50 })}
                  placeholder="50"
                />
              </div>
            </div>

            {!sessionForm.isFree && (
              <div className="pop-field-wrapper" style={{ marginTop: '10px' }}>
                <label>Price (₹): </label>
                <input type="number" min="0" value={sessionForm.price} onChange={e => setSessionForm({ ...sessionForm, price: Math.max(0, parseInt(e.target.value) || 0) })} placeholder="Enter Price" />
              </div>
            )}

            <div className="pop-field-wrapper" style={{ marginTop: '15px' }}>
              <label>Description: </label>
              <textarea rows="4" value={sessionForm.description} onChange={e => setSessionForm({ ...sessionForm, description: e.target.value })} />
            </div>

            {/* Topics Covered */}
            <div className="pop-field-wrapper" style={{ marginTop: '15px' }}>
              <label>
                Topics Covered
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '8px' }}>
                  (Separate with commas)
                </span>
              </label>
              <textarea
                rows={3}
                placeholder="Neural Network Architectures, Convolutional Neural Networks, Transfer Learning, Model Optimization, Real-world Applications"
                value={sessionForm.topics_covered}
                onChange={e => setSessionForm({ ...sessionForm, topics_covered: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <p style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px', marginBottom: 0 }}>
                Each topic will be displayed as a bullet point for students
              </p>
            </div>

            {/* Prerequisites */}
            <div className="pop-field-wrapper" style={{ marginTop: '15px' }}>
              <label>
                Prerequisites
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '8px' }}>
                  (Optional)
                </span>
              </label>
              <textarea
                rows={2}
                placeholder="Basic understanding of Python and machine learning fundamentals"
                value={sessionForm.prerequisites}
                onChange={e => setSessionForm({ ...sessionForm, prerequisites: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Materials */}
            <div className="pop-field-wrapper" style={{ marginTop: '15px' }}>
              <label style={{ marginBottom: '12px', display: 'block' }}>Materials to be Provided</label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  border: `1.5px solid ${sessionForm.materials.slides ? 'var(--cyan)' : 'var(--border)'}`,
                  background: sessionForm.materials.slides ? 'var(--cyan-dim)' : 'var(--engine-black)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: sessionForm.materials.slides ? 'var(--cyan)' : 'var(--text-muted)'
                }}>
                  <input
                    type="checkbox"
                    checked={sessionForm.materials.slides}
                    onChange={e => setSessionForm({
                      ...sessionForm,
                      materials: { ...sessionForm.materials, slides: e.target.checked }
                    })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Slides
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  border: `1.5px solid ${sessionForm.materials.documents ? 'var(--cyan)' : 'var(--border)'}`,
                  background: sessionForm.materials.documents ? 'var(--cyan-dim)' : 'var(--engine-black)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: sessionForm.materials.documents ? 'var(--cyan)' : 'var(--text-muted)'
                }}>
                  <input
                    type="checkbox"
                    checked={sessionForm.materials.documents}
                    onChange={e => setSessionForm({
                      ...sessionForm,
                      materials: { ...sessionForm.materials, documents: e.target.checked }
                    })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Documents & Code Examples
                </label>
              </div>
              {(sessionForm.materials.slides || sessionForm.materials.documents) && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  background: '#eff6ff',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#1e40af',
                  borderLeft: '3px solid #3b82f6'
                }}>
                  ✓ {sessionForm.materials.slides && sessionForm.materials.documents
                    ? 'Slides and documents will be provided'
                    : sessionForm.materials.slides
                      ? 'Slides will be provided'
                      : 'Documents and code examples will be provided'}
                </div>
              )}
            </div>

            <button type="submit" className="pop-btn-primary" style={{ width: '100%', marginTop: '20px' }}>
              {editingSession ? 'Update' : 'Publish'}
            </button>
          </form >
        </div >
      </div >
    );
  };


  const handleUpdateSessionLink = async (session) => {
    const currentLink = session.meeting_link || "";

    setPromptModal({
      show: true,
      title: 'Update Meeting Link',
      message: 'Enter/Update Meeting Link:',
      defaultValue: currentLink,
      onSubmit: async (newLink) => {
        setPromptModal({ show: false, title: '', message: '', defaultValue: '', onSubmit: null });

        if (!newLink || newLink === currentLink) return;

        try {
          if (session.type === 'UNIVERSITY') {
            // Use engagement_id for university sessions if available, otherwise fallback to session.id (though backward compatibility might fail if not updated)
            const targetId = session.engagement_id || session.id;
            await api.put(`/engagement/${targetId}/link?meeting_link=${encodeURIComponent(newLink)}`);
          } else if (session.type === 'INDIVIDUAL') {
            await api.put(`/student-requests/${session.id}/link?meeting_link=${encodeURIComponent(newLink)}`);
          } else {
            showToast("Use the Edit button to change Public Session links.", "info");
            return;
          }
          showToast("Meeting link updated!", "success");
          fetchCombinedSessions();
        } catch (err) {
          console.error(err);
          showToast("Failed to update link", "error");
        }
      }
    });
  };

  const handleMarkComplete = async (sessionId, type) => {
    try {
      const backendTypeMap = {
        'GROUP': 'PUBLIC',
        'UNIVERSITY': 'REQUIREMENT',
        'INDIVIDUAL': 'REQUEST'
      };
      const targetType = backendTypeMap[type] || type;
      await api.put(`/session/mark-complete/${sessionId}?session_type=${targetType}`);
      showToast("Session marked as complete!", "success");
      fetchCombinedSessions();
    } catch (err) {
      console.error("Error marking session complete:", err);
      showToast("Failed to mark session complete", "error");
    }
  };

  const handleViewFeedback = (session) => {
    // Collect all feedback related to this session
    // Mapping frontend types to backend types
    const backendTypeMap = {
      'GROUP': 'PUBLIC',
      'UNIVERSITY': 'PUBLIC', // Student feedback is on the PublicSession
      'INDIVIDUAL': 'REQUEST'
    };

    const targetType = backendTypeMap[session.type] || session.type;
    const sessionFeedbacks = expertFeedback.filter(f => {
      // Direct match
      if (f.session_id === session.id && f.session_type === targetType) return true;

      // For University sessions, also include feedback on the requirement (from the University)
      if (session.type === 'UNIVERSITY' && session.requirement_id) {
        if (f.session_id === session.requirement_id && f.session_type === 'REQUIREMENT') return true;
      }
      return false;
    });

    setSelectedSessionFeedback({
      title: session.title,
      feedbacks: sessionFeedbacks
    });
  };

  const renderFeedbackModal = () => (
    selectedSessionFeedback && (
      <div className="pop-modal-overlay" onClick={() => setSelectedSessionFeedback(null)}>
        <div className="pop-glass-card pop-fade-up" style={{ width: '600px', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)' }}>
              Feedback: {selectedSessionFeedback.title}
            </h3>
            <button className="pop-close-btn" onClick={() => setSelectedSessionFeedback(null)}><XCircle size={22} /></button>
          </div>
          <div style={{ marginTop: '20px' }}>
            {selectedSessionFeedback.feedbacks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {selectedSessionFeedback.feedbacks.map((fb, idx) => (
                  <div key={idx} style={{ padding: '16px', borderRadius: '12px', background: 'var(--engine-black)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{fb.student_name}</div>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star key={star} size={14} fill={star <= fb.quality_rating ? "#f59e0b" : "none"} color="#f59e0b" />
                        ))}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--proto-white)', fontStyle: fb.comments ? 'normal' : 'italic' }}>
                      {fb.comments || "No comments provided."}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '12px' }}>
                      {new Date(fb.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pop-empty-state">
                <MessageSquare size={48} color="var(--border)" />
                <p>No feedback received yet for this session.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );

  const renderSessionTable = (sessions, isPast) => (
    <div className="pop-table-wrapper" style={{ marginTop: '10px', marginBottom: '30px' }}>
      {sessions.length > 0 ? (
        <table className="pop-data-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Session Topic & Participant</th>
              <th>Meeting Link</th>
              <th>Payment Status</th>
              <th>Schedule & Timing</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session, index) => (
              <tr key={index}>
                <td style={{ verticalAlign: 'top', paddingTop: '18px' }}>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    background: session.type === 'GROUP' ? '#eef2ff' : session.type === 'UNIVERSITY' ? '#faf5ff' : '#f0f9ff',
                    color: session.type === 'GROUP' ? '#4f46e5' : session.type === 'UNIVERSITY' ? '#9333ea' : '#0369a1',
                    border: `1px solid ${session.type === 'GROUP' ? '#c7d2fe' : session.type === 'UNIVERSITY' ? '#e9d5ff' : '#bae6fd'}`
                  }}>
                    {session.type}
                  </span>
                </td>
                <td style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                  <div style={{ fontSize: '15px', color: 'var(--proto-white)' }}>
                    {session.title}
                  </div>
                  <div className="td-meta" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', color: 'var(--text-muted)' }}>
                    <Users size={13} /> {session.student_name || session.domain}
                  </div>
                </td>
                <td style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                  {session.meeting_link ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => window.open(session.meeting_link, '_blank')}
                        className="pop-btn-primary sm"
                        style={{ padding: '4px 10px', fontSize: '11px', height: '26px', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Video size={12} /> {isPast ? 'View Link' : 'Join'}
                      </button>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        <button onClick={() => { navigator.clipboard.writeText(session.meeting_link); showToast('Link copied!', 'success'); }} title="Copy Link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Copy size={14} />
                        </button>
                        {session.type !== 'GROUP' && !isPast && (
                          <button onClick={() => handleUpdateSessionLink(session)} title="Edit Link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Pencil size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    session.type !== 'GROUP' && !isPast ? (
                      <button
                        onClick={() => handleUpdateSessionLink(session)}
                        className="pop-btn-outline sm"
                        style={{ fontSize: '11px', padding: '2px 8px', height: '24px', borderStyle: 'dashed' }}
                      >
                        + Add Link
                      </button>
                    ) : (
                      <span style={{ color: 'var(--text-faint)', fontSize: '11px' }}>No Link</span>
                    )
                  )}
                </td>
                <td style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                  {(session.type !== 'UNIVERSITY' && (session.price === 0 || session.is_free)) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span className="status-badge approved" style={{ background: '#ecfdf5', color: '#059669', fontSize: '11px', alignSelf: 'flex-start' }}>
                        Free Session
                      </span>
                      {session.type === 'GROUP' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
                            {session.participant_count || 0} Students Joined
                          </span>
                        </div>
                      )}
                    </div>
                  ) : session.type === 'GROUP' && session.stats ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '6px', background: 'var(--border)', borderRadius: '3px', minWidth: '80px', maxWidth: '120px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            background: session.stats.paid >= session.participant_count ? '#10b981' : '#4f46e5',
                            width: `${(session.stats.paid / Math.max(1, session.participant_count)) * 100}%`
                          }} />
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--proto-white)' }}>
                          {session.stats.paid}/{session.participant_count}
                        </span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>Students Paid</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {(session.payment_status === 'ESCROWED' || session.payment_status === 'HELD' || session.payment_status === 'RELEASED') ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span className="status-badge approved" style={{ background: '#dcfce7', color: '#166534', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-start' }}>
                            <Shield size={12} /> {session.payment_status === 'RELEASED' ? 'Payout Released' : 'Escrow Secured'}
                          </span>
                          {session.payment_status !== 'RELEASED' && (
                            <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: '500' }}>Ready for Payout</span>
                          )}
                        </div>
                      ) : session.payment_status === 'SUCCESS' ? (
                        <span className="status-badge pending" style={{ background: '#fffbeb', color: '#92400e', fontSize: '11px', alignSelf: 'flex-start' }}>
                          Awaiting Escrow
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-faint)', fontWeight: '500' }}>Unpaid / Pending</span>
                      )}
                    </div>
                  )}
                </td>
                <td style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', color: 'var(--proto-white)', fontSize: '13px' }}>
                    <Calendar size={14} color="var(--text-muted)" />
                    {session.is_recurring && session.date_label && session.engagement_type !== 'VISITING'
                      ? session.date_label
                      : formatDate(session.event_date)}
                  </div>
                  <div className="td-meta" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                    <Clock size={13} color="var(--text-muted)" />
                    {session.engagement_type !== 'FRACTIONAL' && (
                      <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>
                        {session.start_time}{session.end_time ? ` - ${session.end_time}` : ''}
                      </span>
                    )}
                    {session.duration && (
                      <span style={{ color: '#4f46e5', fontWeight: '600', background: '#eef2ff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>{session.duration}</span>
                    )}
                    {session.engagement_type === 'FRACTIONAL' && session.weekly_hours && (
                      <span style={{ color: '#0369a1', fontWeight: '600', background: '#e0f2fe', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>{session.weekly_hours}h/week</span>
                    )}
                    {session.days_of_week && session.days_of_week.length > 0 && (
                      <span style={{ color: '#9d174d', fontWeight: '600', background: '#fce7f3', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>
                        {session.days_of_week.map(d => d.substring(0, 3)).join(', ')}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-right" style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {!isPast ? (
                        <>
                          {session.type === 'GROUP' || session.type === 'UNIVERSITY' ? (
                            <>

                              <button
                                onClick={() => handleEditClick(session)}
                                style={{ background: 'var(--engine-black)', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px', color: '#4f46e5', display: 'flex' }}
                                title="Edit Session"
                              >
                                <Pencil size={16} />
                              </button>
                            </>
                          ) : (
                            <button
                              className="pop-btn-outline sm"
                              onClick={() => setSelectedSessionStudents({
                                title: session.title,
                                students: [{ full_name: session.student_name, degree: 'B.Tech / MBA', graduation_year: '2025', escrow_status: session.payment_status }],
                                price: session.price,
                                is_free: session.is_free
                              })}
                              style={{ padding: '6px 12px' }}
                            >
                              Details
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteSession(session.id, session.type)}
                            style={{ background: '#fff1f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px', color: '#ef4444', display: 'flex' }}
                            title="Cancel Session"
                          >
                            <Trash2 size={16} />
                          </button>
                          {session.course_id && (
                            <button
                              onClick={() => {
                                setLmsCourseId(String(session.course_id));
                                setActiveTab('lms');
                              }}
                              className="pop-btn-primary sm"
                              style={{ padding: '6px 12px', background: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}
                              title="Open LMS Course"
                            >
                              <BookOpen size={14} /> View Course
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          className="pop-btn-primary sm"
                          onClick={() => handleViewFeedback(session)}
                          style={{ padding: '6px 12px', background: '#4f46e5' }}
                        >
                          <MessageSquare size={14} style={{ marginRight: '6px' }} /> View Feedback
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {(session.payment_status === 'ESCROWED' || session.payment_status === 'HELD') && !session.is_completed && !isPast && (
                        <button
                          onClick={() => handleMarkComplete(session.type === 'UNIVERSITY' ? session.requirement_id : session.id, session.type)}
                          className="pop-btn-outline sm"
                          style={{ borderColor: '#22c55e', color: '#16a34a', padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <CheckCircle size={12} /> Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="pop-empty-state">
          <Calendar size={48} color="var(--border)" />
          <p>No {isPast ? 'past' : 'upcoming'} sessions found.</p>
        </div>
      )}
    </div>
  );

  const renderMySessionsTab = () => {
    const upcomingSessions = mySessions.filter(session => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const compareDate = session.event_date ? new Date(session.event_date.split('T')[0]) : null;
      if (compareDate) compareDate.setHours(0, 0, 0, 0);

      const isPast = session.is_completed || session.status === 'COMPLETED' || (compareDate && compareDate < today);
      return !isPast;
    }).sort((a, b) => new Date(a.event_date.split('T')[0]) - new Date(b.event_date.split('T')[0]));

    const pastSessions = mySessions.filter(session => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const compareDate = session.event_date ? new Date(session.event_date.split('T')[0]) : null;
      if (compareDate) compareDate.setHours(0, 0, 0, 0);

      const isPast = session.is_completed || session.status === 'COMPLETED' || (compareDate && compareDate < today);
      return isPast;
    }).sort((a, b) => new Date(b.event_date.split('T')[0]) - new Date(a.event_date.split('T')[0]));

    return (
      <div className="pop-fade-up">
        {selectedSessionStudents ? (
          <div className="pop-glass-card">
            <button className="pop-back-link" onClick={() => setSelectedSessionStudents(null)}>← Back to Sessions</button>
            <div className="card-header" style={{ marginTop: '15px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)' }}>
                Roster: {selectedSessionStudents.title}
              </h3>
            </div>
            <div className="pop-table-wrapper" style={{ marginTop: '20px' }}>
              <table className="pop-data-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Educational Context</th>
                    <th>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSessionStudents.students.map((stu, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{stu.full_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Graduation: {stu.graduation_year}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: '13px', color: 'var(--proto-white)' }}>{stu.degree}</div>
                      </td>
                      <td>
                        {stu.escrow_status && ['HELD', 'ESCROWED', 'RELEASED', 'SUCCESS'].includes(stu.escrow_status) ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="status-badge approved" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>
                              <CheckCircle size={12} /> Paid & Secured
                            </span>
                          </div>
                        ) : stu.payment_status === 'SUCCESS' ? (
                          <span className="status-badge pending" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                            Processing
                          </span>
                        ) : (
                          selectedSessionStudents.type !== 'UNIVERSITY' ? (
                            (Number(selectedSessionStudents.price || 0) === 0 || selectedSessionStudents.is_free === true) ? (
                              <span className="status-badge approved" style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0' }}>
                                Free
                              </span>
                            ) : (
                              <span className="status-badge rejected" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
                                Unpaid
                              </span>
                            )
                          ) : (
                            <span className="status-badge" style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <CheckCircle size={12} /> Sponsored
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="pop-glass-card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '4px' }}>Session Commitment Log</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Manage your accepted 1:1, Group, and University sessions.</p>
              </div>
              <div style={{ display: 'inline-flex', background: 'var(--border)', padding: '4px', borderRadius: '8px' }}>
                <button
                  onClick={() => setSessionTab('upcoming')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: sessionTab === 'upcoming' ? '1px solid var(--primary)' : '1px solid transparent',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    background: sessionTab === 'upcoming' ? 'var(--primary-soft)' : 'transparent',
                    color: sessionTab === 'upcoming' ? 'var(--primary)' : 'var(--text-muted)',
                    boxShadow: sessionTab === 'upcoming' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Upcoming
                </button>
                <button
                  onClick={() => setSessionTab('past')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: sessionTab === 'past' ? '1px solid var(--primary)' : '1px solid transparent',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    background: sessionTab === 'past' ? 'var(--primary-soft)' : 'transparent',
                    color: sessionTab === 'past' ? 'var(--primary)' : 'var(--text-muted)',
                    boxShadow: sessionTab === 'past' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Past & History
                </button>
              </div>
            </div>

            {sessionTab === 'upcoming' ? (
              <div className="pop-fade-up">
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--proto-white)', marginTop: '20px', marginBottom: '10px' }}>
                  Upcoming Sessions
                </h4>
                {renderSessionTable(upcomingSessions, false)}
              </div>
            ) : (
              <div className="pop-fade-up">
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--proto-white)', marginTop: '20px', marginBottom: '10px' }}>
                  Past Sessions & History
                </h4>
                {renderSessionTable(pastSessions, true)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };


  return (
    <div className="pop-full-page-wrapper">
      {selectedReqPreview && (
        <div className="pop-modal-overlay">
          <div className="pop-modal-content" style={{ background: 'var(--graphite-dark)', borderRadius: '16px', maxWidth: '600px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '24px 24px 0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: 'var(--proto-white)' }}>Requirement Details</h3>
              <button className="pop-icon-btn-circle" style={{ margin: 0, width: '32px', height: '32px', color: 'var(--text-muted)', background: 'var(--border)', border: 'none' }} onClick={() => setSelectedReqPreview(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', maxHeight: '75vh' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', background: 'var(--engine-black)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Title</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedReqPreview.requirement_title}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>University</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedReqPreview.university_name}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Engagement Type</strong> <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#e0e7ff', color: '#4338ca', fontSize: '12px', fontWeight: '600' }}>{selectedReqPreview.engagement_type}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Delivery Mode</strong> <span style={{ color: '#059669', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Globe size={14} />{selectedReqPreview.delivery_mode}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Budget</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>₹{(selectedReqPreview.total_estimated_amount || selectedReqPreview.budget || 0).toLocaleString()}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Date</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={14} />{formatDate(selectedReqPreview.event_date)} {selectedReqPreview.end_date ? `- ${formatDate(selectedReqPreview.end_date)}` : ''}</span></div>

                {(selectedReqPreview.start_time || selectedReqPreview.end_time) && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Time</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={14} />{selectedReqPreview.start_time || ''}{selectedReqPreview.end_time ? ` - ${selectedReqPreview.end_time}` : ''}</span></div>
                )}

                {selectedReqPreview.days_of_week && selectedReqPreview.days_of_week.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Days of Week</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedReqPreview.days_of_week.join(', ')}</span></div>
                )}

                {selectedReqPreview.engagement_type === 'VISITING' && selectedReqPreview.duration_value && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Duration</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedReqPreview.duration_value} {selectedReqPreview.duration_unit}{selectedReqPreview.duration_value > 1 ? 's' : ''}</span></div>
                )}

                {selectedReqPreview.engagement_type !== 'VISITING' && selectedReqPreview.weekly_hours && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Weekly Hours</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedReqPreview.weekly_hours} hrs</span></div>
                )}
              </div>

              <div style={{ padding: '16px 0', borderTop: '1px solid var(--border)' }}>
                <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={18} color="#6366f1" />
                  Session Description
                </h4>
                <div style={{ background: 'var(--graphite-dark)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', maxHeight: '300px', overflowY: 'auto' }}>
                  <p style={{ whiteSpace: 'pre-line', margin: 0, fontSize: '14.5px', color: 'var(--proto-white)', lineHeight: '1.6' }}>
                    {selectedReqPreview.description || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No detailed description provided for this session.</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedStudentReqPreview && (
        <div className="pop-modal-overlay">
          <div className="pop-modal-content" style={{ background: 'var(--graphite-dark)', borderRadius: '16px', maxWidth: '600px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '24px 24px 0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: 'var(--proto-white)' }}>Student Request Details</h3>
              <button className="pop-icon-btn-circle" style={{ margin: 0, width: '32px', height: '32px', color: 'var(--text-muted)', background: 'var(--border)', border: 'none' }} onClick={() => setSelectedStudentReqPreview(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', maxHeight: '75vh' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', background: 'var(--engine-black)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Title</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedStudentReqPreview.title}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Student</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedStudentReqPreview.student_name}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Session Type</strong> <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#e0e7ff', color: '#4338ca', fontSize: '12px', fontWeight: '600' }}>{selectedStudentReqPreview.session_type}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Date</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={14} />{formatDate(selectedStudentReqPreview.event_date)}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Time</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={14} />{selectedStudentReqPreview.start_time || ''}</span></div>

                {selectedStudentReqPreview.duration && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Duration</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedStudentReqPreview.duration}</span></div>
                )}

                {selectedStudentReqPreview.session_type !== 'INDIVIDUAL' && selectedStudentReqPreview.group_size && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Group Size</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '500' }}>{selectedStudentReqPreview.group_size}</span></div>
                )}

                {selectedStudentReqPreview.price > 0 && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Price</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>₹{selectedStudentReqPreview.price.toLocaleString()}</span></div>
                )}
              </div>

              <div style={{ padding: '16px 0', borderTop: '1px solid var(--border)' }}>
                <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={18} color="#6366f1" />
                  Request Description
                </h4>
                <div style={{ background: 'var(--graphite-dark)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', maxHeight: '300px', overflowY: 'auto' }}>
                  <p style={{ whiteSpace: 'pre-line', margin: 0, fontSize: '14.5px', color: 'var(--proto-white)', lineHeight: '1.6' }}>
                    {selectedStudentReqPreview.description || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No detailed description provided by the student.</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {renderMonthlyModal()}
      {renderFeedbackModal()}
      <aside className="pop-glass-sidebar">
        <nav className="pop-nav-menu">
          <button className={`pop-nav-link ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}><LayoutDashboard size={18} /> Overview</button>
          <button
            id="availability-tab"
            className={`pop-nav-link ${activeTab === 'schedule' ? 'active' : ''} ${!isApproved ? 'disabled' : ''}`}
            onClick={() => isApproved ? setActiveTab('schedule') : showToast('You must be approved through vetting before accessing scheduling.', 'error')}
            disabled={!isApproved}
          >
            <Calendar size={18} /> Schedule {!isApproved && <Shield size={14} style={{ marginLeft: '4px', color: '#f59e0b' }} />}
          </button>
          <button
            className={`pop-nav-link ${activeTab === 'create-session' ? 'active' : ''} ${!isApproved ? 'disabled' : ''}`}
            onClick={() => isApproved ? setActiveTab('create-session') : showToast('You must be approved through vetting before hosting sessions.', 'error')}
            disabled={!isApproved}
          >
            <Plus size={18} /> Host Session {!isApproved && <Shield size={14} style={{ marginLeft: '4px', color: '#f59e0b' }} />}
          </button>
          <button id="engagements-tab" className={`pop-nav-link ${activeTab === 'my-sessions' ? 'active' : ''}`} onClick={() => setActiveTab('my-sessions')}><Video size={18} /> My Sessions</button>
          <button className={`pop-nav-link ${activeTab === 'discover' ? 'active' : ''}`} onClick={() => setActiveTab('discover')}><Search size={18} /> University Posts</button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`pop-nav-link ${activeTab === 'messages' ? 'active' : ''}`}
          >
            <MessageSquare size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>Messages</span>
            {unreadMessagesCount > 0 && (
              <span style={{
                background: '#ef4444',
                color: 'white',
                fontSize: '11px',
                fontWeight: '600',
                padding: '2px 8px',
                borderRadius: '999px',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {unreadMessagesCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('discussions')}
            className={`pop-nav-link ${activeTab === 'discussions' ? 'active' : ''}`}
          >
            <Users size={18} /> Groups
          </button>

          <button
            onClick={() => setActiveTab('lms')}
            className={`pop-nav-link ${activeTab === 'lms' ? 'active' : ''}`}
          >
            <BookOpen size={18} /> LMS
          </button>
          <button className={`pop-nav-link ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}><User size={18} /> Profile</button>
        </nav>

      </aside>

      <main className="pop-fluid-content">
        <header className="pop-glass-header">
          <div className="pop-header-context">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1>{activeTab === 'lms' ? 'LMS' : (activeTab === 'discussions' ? 'GROUPS' : activeTab.replace('-', ' ').toUpperCase())}</h1>
              {profile.role === 'pop' || activeTab ? (
                  <span style={{ 
                      padding: '4px 10px', 
                      borderRadius: '6px', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      background: profile.years_of_experience >= 10 ? '#dcfce7' : '#dbeafe', 
                      color: profile.years_of_experience >= 10 ? '#166534' : '#1e40af', 
                      border: `1px solid ${profile.years_of_experience >= 10 ? '#bbf7d0' : '#bfdbfe'}` 
                  }}>
                      {profile.years_of_experience >= 10 ? 'Professor of Practice' : 'Instructor'}
                  </span>
              ) : null}
            </div>
            <p className="pop-header-sub">Status: <span className={isVisible ? 'text-green' : 'text-muted'}>● {isVisible ? 'Online' : 'Draft Mode'}</span></p>
          </div>
          <div className="pop-header-actions">
            <div style={{ position: 'relative' }}>
              <button className="pop-icon-btn-circle" onClick={() => setShowNotifs(!showNotifs)} title="Notifications">
                <Bell size={20} />
                {notifications.some(n => !n.is_read) && <span className="notif-dot" style={{ position: 'absolute', top: 0, right: 0, width: '10px', height: '10px', background: '#ef4444', borderRadius: '50%', border: '2px solid var(--bg-card)' }}></span>}
              </button>
              {showNotifs && (
                <div className="pop-notif-dropdown" style={{ position: 'absolute', top: '50px', right: 0, width: '300px', background: 'var(--graphite-dark)', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 100, padding: '16px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Notifications</h4>
                      {notifications.some(n => !n.is_read) && (
                        <button onClick={handleMarkAllAsRead} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '11px', color: 'var(--cyan)' }}>
                          Mark all as read
                        </button>
                      )}
                    </div>
                    <button onClick={() => setShowNotifs(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <XCircle size={16} color="var(--text-muted)" />
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.filter(n => !n.is_read).length > 0 ? notifications.filter(n => !n.is_read).map(n => (
                      <div key={n.id} style={{ padding: '10px', borderBottom: '1px solid var(--border)', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: 'rgba(59, 130, 246, 0.05)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', display: 'inline-block', flexShrink: 0 }}></span>
                            {n.title}
                          </div>
                          <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{n.message}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                        <button onClick={() => handleMarkAsRead(n.id)} title="Mark as read" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)' }}>
                          <CheckCircle size={14} />
                        </button>
                      </div>
                    )) : <p style={{ fontSize: '13px', color: 'var(--text-faint)', textAlign: 'center', padding: '10px' }}>No new notifications</p>}
                  </div>
                </div>
              )}
            </div>
            <button className="pop-icon-btn-circle" onClick={() => setIsCalendarModalOpen(true)} title="Monthly Calendar">
              <Calendar size={20} />
            </button>
          </div>
          <OnboardingTour user={profile} role="pop" />
        </header>

        <div className="pop-scroll-container">
          {activeTab === 'dashboard' && (
            <div className="pop-dashboard-grid pop-fade-up">
              {!isVisible && (
                <div className="draft-mode-banner" style={{
                  background: 'rgba(99, 102, 241, 0.05)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  gridColumn: '1 / -1',
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.05)'
                }}>
                  <div style={{ background: 'rgba(99, 102, 241, 0.15)', padding: '10px', borderRadius: '50%', color: '#6366f1', display: 'flex', flexShrink: 0 }}>
                    <Info size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 6px 0', color: 'var(--text-primary)', fontSize: '1.05rem', fontWeight: '600' }}>Your Profile is in Draft Mode</h4>
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                      Your profile is currently hidden from universities and students. To start receiving engagements, go to the{' '}
                      <span 
                        onClick={() => setActiveTab('profile')}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#e0e7ff',
                          color: '#4338ca',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          border: '1px solid #c7d2fe',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(99, 102, 241, 0.1)'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#c7d2fe';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = '#e0e7ff';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <Settings size={14} /> Settings
                      </span>{' '}
                      tab and click <strong>Publish Profile</strong>.
                    </p>
                  </div>
                </div>
              )}
              <section className="pop-stats-row">
                <div className="pop-metric-card" style={{ position: 'relative' }}>
                  <Inbox className="text-indigo" size={20} />
                  <CardInfoTooltip title="Engagement Invites" content="Total pending invites from universities." />
                  <div className="metric-body">
                    <span className="metric-value">{stats.total}</span>
                    <span className="metric-label" style={{ marginLeft: '8px' }}>Invites</span>
                  </div>
                </div>
                <div className="pop-metric-card"><CheckCircle className="text-green" size={20} /><div className="metric-body"><span className="metric-value">{stats.accepted}</span><span className="metric-label" style={{ marginLeft: '8px' }}>Confirmed</span></div></div>
                <div className="pop-metric-card"><Award className="text-amber" size={20} /><div className="metric-body"><span className="metric-value">₹{stats.earnings.toLocaleString()}</span><span className="metric-label" style={{ marginLeft: '8px' }}>Earnings</span></div></div>
              </section>

              {/* Interview Notification Card (Vetting) */}
              {applicationStatus?.interview_status === 'SCHEDULED' && applicationStatus?.interview_timeslot && (
                <div className="interview-info-card pop-fade-up">
                  <div className="interview-card-details">
                    <h4>Onboarding Scheduled</h4>
                    <div className="interview-time">
                      <Calendar size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      {new Date(applicationStatus.interview_timeslot).toLocaleString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    {applicationStatus.interview_meeting_link && (
                      <div className="interview-link-area">
                        <Video size={14} />
                        <span>Link: {applicationStatus.interview_meeting_link}</span>
                      </div>
                    )}
                  </div>
                  <div className="interview-card-action">
                    {(() => {
                      const now = new Date();
                      const scheduledTime = new Date(applicationStatus.interview_timeslot);
                      const startTimeWindow = new Date(scheduledTime.getTime() - 10 * 60000); // 10 mins before
                      const endTimeWindow = new Date(scheduledTime.getTime() + 120 * 60000); // 2 hours after
                      const isLive = now >= startTimeWindow && now <= endTimeWindow;

                      return (
                        <button
                          className={`btn-join-interview ${isLive ? 'active' : 'disabled'}`}
                          onClick={() => isLive && window.open(applicationStatus.interview_meeting_link, '_blank')}
                          title={isLive ? "Join the interview now" : "Button will be active 10 minutes before the scheduled time"}
                        >
                          <Video size={20} />
                          {isLive ? "Join Now" : "Join Now (Inactive)"}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              )}

                  {/* Conflict of Interest (COI) Card - Show after vetting and KYC are approved and not yet signed */}
                  {applicationStatus?.status === 'APPROVED' && profile?.kyc_status === 'ACCEPTED' && applicationStatus?.coi_status !== 'SIGNED' && (
                    <div className="pop-fade-up" style={{ marginTop: '16px', marginBottom: '24px', padding: '20px', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-card)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                      <div className="interview-card-details">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                          <h4 style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.1rem', margin: 0, fontWeight: '700' }}>
                            <div style={{ background: 'var(--primary-soft)', padding: '8px', borderRadius: '8px', color: 'var(--primary)', display: 'flex' }}>
                              <FileCheck size={20} />
                            </div>
                            Conflict of Interest Declaration
                          </h4>
                          <button
                            type="button"
                            className="pop-btn-outline"
                            onClick={() => downloadCoiDisclaimer('download')}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', borderColor: 'var(--border)' }}
                          >
                            <Download size={16} /> Download Terms
                          </button>
                        </div>
                        
                        <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                          This is the final step to activate your profile. Please review the Conflict of Interest (CoI) and Employment Disclosure form, then digitally sign below to complete your onboarding.
                        </p>

                        {applicationStatus?.coi_status === 'SIGNED' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: '600', padding: '12px', background: 'rgba(16, 185, 129, 0.08)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                            <CheckCircle size={20} /> Declaration Digitally Signed Successfully
                          </div>
                        ) : (
                          <div className="coi-actions" style={{ background: 'var(--bg-body)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                            
                            <label 
                              htmlFor="coi-agree" 
                              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', margin: 0 }}
                            >
                              <div style={{ paddingTop: '1px' }}>
                                <input 
                                  type="checkbox" 
                                  id="coi-agree"
                                  checked={coiAgreed}
                                  onChange={(e) => setCoiAgreed(e.target.checked)}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--primary)' }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px', fontSize: '0.95rem', lineHeight: '1.4', textTransform: 'none' }}>
                                  I, <span style={{fontWeight: '700', color: 'var(--primary)', textTransform: 'none'}}>{profile?.full_name || 'Professor'}</span>, hereby declare and agree to the Conflict of Interest terms as set forth by EdVergenceX.
                                </div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'none' }}>
                                  By checking this box and submitting, a digitally signed document will be generated and securely stored on your profile.
                                </div>
                              </div>
                            </label>

                            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                              <button
                                type="button"
                                className="pop-btn-primary"
                                style={{ 
                                  display: 'flex', alignItems: 'center', gap: '8px', 
                                  padding: '10px 24px',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  borderRadius: '6px',
                                  opacity: coiAgreed ? 1 : 0.6,
                                  cursor: coiAgreed ? 'pointer' : 'not-allowed',
                                  transition: 'all 0.2s ease'
                                }}
                                disabled={uploadingCoi || !coiAgreed}
                                onClick={handleSignCoi}
                              >
                                {uploadingCoi ? <RefreshCw className="spin" size={18} /> : <CheckCircle size={18} />}
                                Sign & Submit Declaration
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

              <div className="pop-main-grid-full">
                <section className="pop-content-column-full">
                  {/* Status Overview Blocks - Only show if not both approved */}
                  {!(applicationStatus?.status === 'APPROVED' && profile.kyc_status === 'ACCEPTED') && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>

                      {/* Vetting Status Block - Only show if not approved */}
                      {applicationStatus?.status !== 'APPROVED' && (
                        <div className="pop-glass-card" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            background: applicationStatus?.status === 'DECLINED' ? '#fee2e2' : '#ffedd5',
                            color: applicationStatus?.status === 'DECLINED' ? '#dc2626' : '#ea580c',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {applicationStatus?.status === 'DECLINED' ? <XCircle size={20} /> : <Clock size={20} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>Vetting Status</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--proto-white)' }}>
                              {applicationStatus?.status ? applicationStatus.status.replace('_', ' ') : 'NOT SUBMITTED'}
                            </div>
                            {!applicationStatus?.has_application && (
                              <button
                                className="pop-btn-primary"
                                style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px' }}
                                onClick={() => { setActiveTab('profile'); setSettingsTab('documents'); }}
                              >
                                <Upload size={14} style={{ marginRight: '4px' }} />
                                Click here to upload
                              </button>
                            )}
                            {applicationStatus?.status === 'DECLINED' && (
                              <button
                                className="pop-btn-primary"
                                style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px', background: '#dc2626', borderColor: '#dc2626' }}
                                onClick={() => { setActiveTab('profile'); setSettingsTab('documents'); }}
                              >
                                <Upload size={14} style={{ marginRight: '4px' }} />
                                Replace Documents
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* KYC Status Block - Only show if not accepted */}
                      {profile.kyc_status !== 'ACCEPTED' && (
                        <div className="pop-glass-card" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            background: profile.kyc_status === 'REJECTED' ? '#fee2e2' : '#ffedd5',
                            color: profile.kyc_status === 'REJECTED' ? '#dc2626' : '#ea580c',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {profile.kyc_status === 'REJECTED' ? <XCircle size={20} /> : <Clock size={20} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>KYC Status</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--proto-white)' }}>
                              {profile.kyc_status ? profile.kyc_status.replace('_', ' ') : 'PENDING'}
                            </div>
                            {(!documentStatus.pan_card || !documentStatus.aadhaar_card) && (
                              <button
                                className="pop-btn-primary"
                                style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px' }}
                                onClick={() => { setActiveTab('profile'); setSettingsTab('documents'); }}
                              >
                                <Upload size={14} style={{ marginRight: '4px' }} />
                                Click here to upload
                              </button>
                            )}
                            {profile.kyc_status === 'REJECTED' && (
                              <button
                                className="pop-btn-primary"
                                style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px', background: '#dc2626', borderColor: '#dc2626' }}
                                onClick={() => { setActiveTab('profile'); setSettingsTab('documents'); }}
                              >
                                <Upload size={14} style={{ marginRight: '4px' }} />
                                Replace Documents
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                    </div>
                  )}
                  <div className="pop-glass-card" style={{ position: 'relative' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Recent University Requests</h3>
                      <div className="preferred-locations-context" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        Your Preferred: {preferredLocations.join(', ') || 'None set'}
                      </div>
                    </div>
                    <CardInfoTooltip title="Recent University Requests" content="Manage pending job offers and engagement requests directly sent by universities." />
                    <div className="pop-table-wrapper">
                      {requests.length > 0 ? (
                        <table className="pop-data-table">
                          <thead><tr><th>Partner</th><th style={{ textAlign: 'center' }}>Budget</th><th>Time</th><th>Status</th><th className="text-right"></th></tr></thead>
                          <tbody>
                            {requests.filter(req => !req.is_completed).map(req => (
                              <tr key={req.id} onClick={() => setSelectedReqPreview(req)} style={{ cursor: 'pointer' }} className="hoverable-row">
                                <td>
                                  <div className="td-primary">{req.requirement_title}</div>
                                  <div className="td-meta-row">
                                    <span className="td-university">{req.university_name}</span>
                                    {req.status === 'ACCEPTED' && req.university_email && (
                                      <div style={{ marginTop: '2px' }}>
                                        <span className="td-email" style={{ fontSize: '11px', color: '#4f46e5', fontWeight: '500' }}>
                                          {req.university_email}
                                        </span>
                                        {req.delivery_mode === 'Remote' && !req.meeting_link && (
                                          <span style={{ fontSize: '10px', color: '#dc2626', marginLeft: '6px' }}>← Provide Meeting Link</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ marginTop: '4px', display: 'flex', gap: '6px' }}>
                                    <span style={{
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      background: req.delivery_mode === 'Remote' ? '#dcfce7' : 'var(--border)',
                                      color: req.delivery_mode === 'Remote' ? '#15803d' : 'var(--text-muted)',
                                      fontWeight: '600'
                                    }}>
                                      {req.delivery_mode || 'TBD'}
                                    </span>
                                    {req.engagement_type && (
                                      <span style={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        background: '#fef3c7',
                                        color: '#854d0e',
                                        fontWeight: '600',
                                        textTransform: 'uppercase'
                                      }}>
                                        {req.engagement_type}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ fontWeight: '700', color: '#10b981', fontSize: '15px' }}>
                                    ₹{(req.total_estimated_amount || req.budget || 0).toLocaleString()}
                                  </div>
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                    {req.budget ? 'Fixed Budget' : (req.hourly_rate ? `Est. @ ₹${req.hourly_rate}/hr` : 'Est. Amount')}
                                  </div>
                                </td>
                                <td>
                                  <div className="td-secondary">
                                    {formatDate(req.event_date)}
                                    {req.end_date && ` - ${formatDate(req.end_date)}`}
                                  </div>
                                  <div className="td-meta" style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                    {req.start_time && req.engagement_type !== 'FRACTIONAL' && (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Clock size={12} /> {req.start_time}{req.end_time ? ` - ${req.end_time}` : ''}
                                      </span>
                                    )}
                                    {req.days_of_week && req.days_of_week.length > 0 && (
                                      <span style={{ color: '#4f46e5', fontWeight: '500' }}>
                                        {req.days_of_week.map(d => d.substring(0, 3)).join(', ')}
                                      </span>
                                    )}
                                    {req.engagement_type === 'FRACTIONAL' && req.weekly_hours && (
                                      <span style={{ color: '#0369a1' }}>
                                        Quota: {req.weekly_hours}h/week
                                      </span>
                                    )}
                                    {(req.duration_unit !== 'Hours' || req.engagement_type === 'VISITING') && req.duration_value && (
                                      <span>({req.duration_value} {req.duration_unit})</span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span className={`status-pill ${req.status.toLowerCase()}`}>{req.status}</span>
                                  {req.status === 'ACCEPTED' && req.meeting_link && (
                                    <div style={{ fontSize: '10px', color: '#059669', marginTop: '4px' }}>Link Set</div>
                                  )}
                                </td>
                                <td className="text-right">
                                  {['requested', 'pending'].includes(req.status.toLowerCase()) ? (
                                    <div className="pop-table-actions">
                                      <button className="pop-action-decline" onClick={(e) => { e.stopPropagation(); handleRequestAction(req.id, 'Declined'); }} disabled={processing === req.id}><X size={18} /></button>
                                      <button className="pop-action-accept" onClick={(e) => { e.stopPropagation(); handleRequestAction(req.id, 'Accepted'); }} disabled={processing === req.id}><Check size={18} /></button>
                                    </div>
                                  ) : (req.status === 'ACCEPTED' && req.delivery_mode === 'Remote') ? (
                                    <button
                                      className="pop-btn-primary"
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPromptModal({
                                          show: true,
                                          title: 'Set Meeting Link',
                                          message: 'Enter Meeting Link (Zoom/Google Meet/etc.)',
                                          defaultValue: req.meeting_link || '',
                                          onSubmit: async (url) => {
                                            setPromptModal({ show: false, title: '', message: '', defaultValue: '', onSubmit: null });
                                            if (!url || url.trim() === '') return;
                                            try {
                                              await api.put(`/engagement/${req.id}/link?meeting_link=${encodeURIComponent(url)}`);
                                              showToast("Meeting link updated! Students of this university will now see it under 'University Posts'.", 'success');
                                              fetchData();
                                            } catch (e) {
                                              showToast("Failed to update link", 'error');
                                            }
                                          }
                                        });
                                      }}
                                    >
                                      {req.meeting_link ? "Update Link" : "Set Link"}
                                    </button>
                                  ) : <span className="td-action-complete">Handled</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="pop-empty-state-modern">
                          <div className="empty-icon-circle blue"><Zap size={24} /></div>
                          <p>No university requests.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pop-glass-card" style={{ marginTop: '24px', position: 'relative' }}>
                    <div className="card-header">
                      <h3>Incoming Student Requests</h3>
                    </div>
                    <CardInfoTooltip title="Incoming Student Requests" content="Review and accept 1-on-1 session requests initiated by students." />
                    <div className="pop-table-wrapper">
                      {studentRequests.length > 0 ? (
                        <table className="pop-data-table">
                          <thead><tr><th>Student & Topic</th><th>Type</th><th>Time</th><th className="text-right"></th></tr></thead>
                          <tbody>
                            {studentRequests.map(req => (
                              <tr key={req.id} onClick={() => setSelectedStudentReqPreview(req)} style={{ cursor: 'pointer' }} className="hoverable-row">
                                <td><div className="td-primary">{req.title}</div><div className="td-meta">By: {req.student_name}</div></td>
                                <td><span className={`status-pill ${req.session_type.toLowerCase()}`}>{req.session_type}</span></td>
                                <td><div>{formatDate(req.event_date)}</div><div className="td-meta">{req.start_time}</div></td>
                                <td className="text-right">
                                  {req.status.toLowerCase() === 'pending' ? (
                                    <div className="pop-table-actions">
                                      <button className="pop-action-decline" onClick={(e) => { e.stopPropagation(); handleStudentRequestAction(req.id, 'Declined'); }} disabled={processing === req.id}><XCircle size={18} /></button>
                                      <button className="pop-action-accept" onClick={(e) => { e.stopPropagation(); handleStudentRequestAction(req.id, 'Accepted'); }} disabled={processing === req.id}><CheckCircle size={18} /></button>
                                    </div>
                                  ) : <span className={`status-pill ${req.status.toLowerCase()}`}>{req.status}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="pop-empty-state-modern">
                          <div className="empty-icon-circle purple"><Users size={24} /></div>
                          <p>No student requests.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          )}
          {activeTab === 'schedule' && renderScheduleTab()}
          {activeTab === 'create-session' && renderCreateSessionTab()}
          {activeTab === 'my-sessions' && renderMySessionsTab()}
          {activeTab === 'discover' && <DiscoverOpportunities />}
          {activeTab === 'discussions' && <DiscussionsPage embedded={true} />}
          {activeTab === 'messages' && <ChatPage embedded={true} />}
          {activeTab === 'lms' && (
            <div className="pop-fade-up" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <PopLMSSection initialCourseId={lmsCourseId} key={lmsCourseId} />
            </div>
          )}

          {
            activeTab === 'profile' && (
              <div className="pop-profile-layout pop-fade-up">
                <div className="pop-settings-header-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                      <h2 className="settings-page-title">Profile & Settings</h2>
                      <p className="settings-page-subtitle">Manage your professional identity, engagement preferences, and verification documents.</p>
                    </div>
                    <div className="pop-profile-actions-inline" style={{ position: 'relative' }}>
                      {/* Publish Profile Notification */}
                      {showPublishNotification && !isVisible && (
                        <div className="publish-notification-tooltip">
                          <button
                            className="notification-close-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPublishNotification(false);
                            }}
                            aria-label="Dismiss notification"
                          >
                            ×
                          </button>
                          <div className="notification-content">
                            <strong>Ready to go live?</strong>
                            <p>Click "Publish Profile" to make your profile visible to students and universities!</p>
                          </div>
                          <div className="notification-arrow"></div>
                        </div>
                      )}
                      <button className="pop-btn-secondary" onClick={() => handleSaveProfile(false)}>Save Draft</button>
                      <button className={`pop-btn-primary ${isVisible ? 'btn-live' : ''}`} onClick={() => handleSaveProfile(true)}>
                        {isVisible ? 'Update Live Profile' : 'Publish Profile'}
                      </button>
                    </div>
                  </div>

                  <div className="pop-settings-links">
                    <button className={`pop-tab-link ${settingsTab === 'general' ? 'active' : ''}`} onClick={() => setSettingsTab('general')}>
                      <User size={18} /> General Profile
                    </button>
                    <button className={`pop-tab-link ${settingsTab === 'preferences' ? 'active' : ''}`} onClick={() => setSettingsTab('preferences')}>
                      <Briefcase size={18} /> Preferences & Pricing
                    </button>
                    <button className={`pop-tab-link ${settingsTab === 'documents' ? 'active' : ''}`} onClick={() => setSettingsTab('documents')}>
                      <FileCheck size={18} /> Documents & Verification
                    </button>
                    <button className={`pop-tab-link ${settingsTab === 'bank-details' ? 'active' : ''}`} onClick={() => setSettingsTab('bank-details')}>
                      <CreditCard size={18} /> Bank Details
                    </button>
                  </div>
                </div>

                <div className="pop-settings-content-wrapper">
                  {settingsTab === 'general' && (
                    <div className="pop-fade-up">
                      <div className="pop-glass-card">
                        <div className="card-header"><h3>Professional Identity</h3></div>
                        <div className="pop-profile-form-vertical">
                          {/* Profile Photo Section */}
                          <div className="pop-field-wrapper" style={{ marginBottom: '24px' }}>
                            <label>Profile Picture</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginTop: '12px' }}>
                              <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                background: 'var(--border)',
                                border: '2px solid var(--border)',
                                position: 'relative'
                              }}>
                                {profile.profile_photo_path ? (
                                  <img
                                    src={profile.profile_photo_path.startsWith('http') ? profile.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${profile.profile_photo_path.startsWith('/') ? '' : '/'}${profile.profile_photo_path}`}
                                    alt="Profile"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "https://ui-avatars.com/api/?name=" + profile.full_name;
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '32px',
                                    fontWeight: 'bold',
                                    color: 'var(--text-muted)',
                                    background: 'var(--border)'
                                  }}>
                                    {profile.full_name?.charAt(0) || <User size={40} />}
                                  </div>
                                )}
                                {uploadingPhoto && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(255, 255, 255, 0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <RefreshCw className="spin" size={24} color="#3b82f6" />
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <input
                                    type="file"
                                    id="profile-photo-input"
                                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                                    accept="image/*"
                                    onChange={(e) => handlePhotoUpload(e.target.files[0])}
                                  />
                                  <button
                                    className="pop-btn-primary"
                                    style={{ fontSize: '13px', padding: '8px 16px' }}
                                    onClick={() => document.getElementById('profile-photo-input').click()}
                                    disabled={uploadingPhoto}
                                  >
                                    <Upload size={14} style={{ marginRight: '6px' }} />
                                    {profile.profile_photo_path ? 'Change Photo' : 'Upload Photo'}
                                  </button>
                                  {profile.profile_photo_path && (
                                    <button
                                      className="pop-btn-outline"
                                      style={{ fontSize: '13px', padding: '8px 16px', borderColor: '#ef4444', color: '#ef4444' }}
                                      onClick={handlePhotoDelete}
                                      disabled={uploadingPhoto}
                                    >
                                      <Trash2 size={14} style={{ marginRight: '6px' }} />
                                      Remove
                                    </button>
                                  )}
                                </div>
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
                                  Recommended: Square image, max 5MB (JPEG, PNG, WEBP)
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="pop-field-wrapper">
                            <label>Full Name</label>
                            <input value={profile.full_name || ''} onChange={e => setProfile({ ...profile, full_name: e.target.value })} placeholder="Your Name" />
                          </div>
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Phone Number</label>
                            <div className="phone-input-container">
                              <PhoneInput
                                country={'in'}
                                value={profile.phone_number || ''}
                                onChange={value => setProfile({ ...profile, phone_number: value })}
                                enableSearch={true}
                                disableSearchIcon={true}
                                placeholder="Phone Number"
                                inputProps={{
                                  name: 'phone_number',
                                  required: true,
                                }}
                                containerClass="react-tel-input"
                                inputClass="pop-input"
                                style={{ width: '100%' }}
                                buttonStyle={{ borderRadius: '8px 0 0 8px', border: '1px solid var(--border)', borderRight: 'none', background: 'var(--graphite-dark)' }}
                                inputStyle={{ width: '100%', padding: '10px 10px 10px 48px', borderRadius: '8px', border: '1px solid var(--border)', height: '42px' }}
                              />
                            </div>
                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Updating here will update your verified contact number.</p>
                          </div>
                          <div className="pop-grid-half" style={{ marginTop: '16px' }}>
                            <div className="pop-field-wrapper">
                              <label>Job Title</label>
                              <input 
                                value={profile.job_title || ''} 
                                onChange={e => setProfile({ ...profile, job_title: e.target.value })} 
                                placeholder="e.g. Lead Researcher" 
                                style={profile.job_title && !/^[A-Za-z\s\.\-\&]+$/.test(profile.job_title.trim()) ? { borderColor: 'red' } : {}}
                              />
                              {profile.job_title && !/^[A-Za-z\s\.\-\&]+$/.test(profile.job_title.trim()) && <span style={{color: 'red', fontSize: '12px'}}>Job title can only contain letters and valid characters.</span>}
                            </div>
                            <div className="pop-field-wrapper">
                              <label>Current Company / University</label>
                              <input value={profile.company || ''} onChange={e => setProfile({ ...profile, company: e.target.value })} placeholder="e.g. Google DeepMind" />
                            </div>
                          </div>
                          <div className="pop-grid-half" style={{ marginTop: '16px' }}>
                            <div className="pop-field-wrapper">
                              <label>Industry Domain</label>
                              <select value={profile.domain || ''} onChange={e => setProfile({ ...profile, domain: e.target.value })}>
                                <option value="">Select Domain</option>
                                {INDUSTRY_DOMAINS.map(domain => (
                                  <option key={domain} value={domain}>{domain}</option>
                                ))}
                              </select>
                            </div>
                            <div className="pop-field-wrapper">
                              <label>Years of Experience</label>
                              <input 
                                type="number" 
                                min="0" 
                                max="70"
                                value={profile.years_of_experience !== undefined ? profile.years_of_experience : ''} 
                                onChange={e => {
                                  let val = e.target.value;
                                  if (val !== '') {
                                    const parsed = parseInt(val, 10);
                                    if (isNaN(parsed) || parsed < 0) val = '0';
                                    else if (parsed > 70) val = '70';
                                  }
                                  setProfile({ ...profile, years_of_experience: val });
                                }} 
                                onKeyDown={(e) => {
                                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault();
                                  }
                                }}
                                placeholder="e.g. 5" 
                                style={profile.years_of_experience !== undefined && parseInt(profile.years_of_experience) < 0 ? { borderColor: 'red' } : {}}
                              />
                              {profile.years_of_experience !== undefined && parseInt(profile.years_of_experience) < 0 && <span style={{color: 'red', fontSize: '12px'}}>Years of experience must be 0 or greater.</span>}
                            </div>
                          </div>
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Professional Background Category</label>
                            <select
                              value={backgroundCategory}
                              onChange={e => {
                                setBackgroundCategory(e.target.value);
                              }}
                            >
                              <option value="">Select Background Category</option>
                              {POP_BACKGROUND_CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          
                          {/* EXPERTISE TAXONOMY */}
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Expertise Category</label>
                            <select
                              value={expertiseCategory}
                              onChange={(e) => {
                                setExpertiseCategory(e.target.value);
                                setExpertiseSubcategory('');
                                setExpertiseCore('');
                              }}
                            >
                              <option value="">Select Category...</option>
                              {dynIndustries.length > 0
                                ? dynIndustries.map(cat => (
                                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                                  ))
                                : Object.keys(EXPERTISE_TAXONOMY).map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                  ))}
                            </select>
                          </div>

                          <div className="pop-grid-2" style={{ marginTop: '16px' }}>
                            <div className="pop-field-wrapper">
                              <label>Expertise Sub-category</label>
                              <select
                                value={expertiseSubcategory}
                                onChange={(e) => {
                                  setExpertiseSubcategory(e.target.value);
                                  setExpertiseCore('');
                                }}
                                disabled={!expertiseCategory}
                              >
                                <option value="">Select Sub-category...</option>
                                {dynCategories.length > 0
                                  ? dynCategories.map(sub => (
                                      <option key={sub.id} value={sub.name}>{sub.name}</option>
                                    ))
                                  : expertiseCategory && EXPERTISE_TAXONOMY[expertiseCategory]
                                    ? Object.keys(EXPERTISE_TAXONOMY[expertiseCategory]).map(sub => (
                                        <option key={sub} value={sub}>{sub}</option>
                                      ))
                                    : null}
                              </select>
                            </div>

                            <div className="pop-field-wrapper">
                              <label>Core Expertise</label>
                              <select
                                value={expertiseCore}
                                onChange={(e) => setExpertiseCore(e.target.value)}
                                disabled={!expertiseSubcategory}
                              >
                                <option value="">Select Core Expertise...</option>
                                {dynSubcategories.length > 0
                                  ? dynSubcategories.map(core => (
                                      <option key={core.id} value={core.name}>{core.name}</option>
                                    ))
                                  : expertiseCategory && expertiseSubcategory && EXPERTISE_TAXONOMY[expertiseCategory]?.[expertiseSubcategory]
                                    ? EXPERTISE_TAXONOMY[expertiseCategory][expertiseSubcategory].map(core => (
                                        <option key={core} value={core}>{core}</option>
                                      ))
                                    : null}
                              </select>
                            </div>
                          </div>
                          {/* END EXPERTISE TAXONOMY */}
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Highest Education</label>
                            <input value={highestEducation} onChange={e => setHighestEducation(e.target.value)} placeholder="e.g. PhD in Computer Science" />
                          </div>
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Location</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <select 
                                value={isOtherLocation ? 'Other' : (profile.city || '')} 
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val === 'Other') {
                                    setIsOtherLocation(true);
                                    setProfile({ ...profile, city: '' });
                                  } else {
                                    setIsOtherLocation(false);
                                    setProfile({ ...profile, city: val, state: '', country: '' });
                                  }
                                }}
                              >
                                <option value="">Select Location</option>
                                {ALL_LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                                <option value="Other">Other (Specify)</option>
                              </select>
                              {isOtherLocation && (
                                <input 
                                  value={profile.city || ''} 
                                  onChange={e => setProfile({ ...profile, city: e.target.value })} 
                                  placeholder="Enter custom location" 
                                />
                              )}
                            </div>
                          </div>
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Professional Bio</label>
                            <textarea value={profile.bio || ''} onChange={e => setProfile({ ...profile, bio: e.target.value })} placeholder="Brief summary of your expertise..." rows={3} />
                          </div>
                          <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                            <label>Core Expertise Tags</label>
                            <SkillsInput selectedSkills={myExpertise} onSkillsChange={setMyExpertise} />
                          </div>
                          <div className="pop-grid-half" style={{ marginTop: '16px' }}>
                            <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                              <label>LinkedIn URL <span style={{color: 'red'}}>*</span></label>
                              <div className="input-with-icon">
                                <Globe size={14} />
                                <input 
                                  value={linkedin} 
                                  onChange={e => setLinkedin(e.target.value)} 
                                  placeholder="https://www.linkedin.com/in/username" 
                                  style={(!linkedin || !/^https?:\/\/(www\.)?linkedin\.com\/.*$/.test(linkedin.trim())) ? { borderColor: 'red' } : {}}
                                />
                              </div>
                              {(!linkedin || !/^https?:\/\/(www\.)?linkedin\.com\/.*$/.test(linkedin.trim())) && <span style={{color: 'red', fontSize: '12px'}}>Valid LinkedIn URL is required.</span>}
                            </div>
                            <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                              <label>Resume Upload</label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input 
                                  type="file" 
                                  accept=".pdf,.doc,.docx" 
                                  id="resume-upload"
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                      setResumeFile(e.target.files[0]);
                                      setResume(e.target.files[0].name);
                                    }
                                  }}
                                />
                                <button 
                                  type="button"
                                  className="pop-btn-primary sm"
                                  onClick={() => document.getElementById('resume-upload').click()}
                                >
                                  Choose File
                                </button>
                                {resume && (
                                  <span style={{ fontSize: '14px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FileText size={16} /> {resume}
                                    <X size={16} style={{ cursor: 'pointer', color: 'red' }} onClick={() => { setResumeFile(null); setResume(''); }} />
                                  </span>
                                )}
                              </div>
                              <p style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '4px' }}>Max 5MB. PDF, DOC, DOCX.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Security - Change Password */}
                      <div className="pop-glass-card" style={{ marginTop: '24px' }}>
                        <div className="card-header"><h3>Security</h3></div>
                        <div className="pop-settings-block">
                          <label className="pop-block-label">Password</label>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                              ••••••••••••
                            </div>
                            <button
                              onClick={() => setShowChangePasswordModal(true)}
                              className="pop-btn-secondary"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 16px'
                              }}
                            >
                              <Lock size={14} />
                              Change Password
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="pop-glass-card" style={{ marginTop: '24px' }}>
                        <div className="card-header"><h3>Account Actions</h3></div>
                        <div className="account-settings-section">
                          {deactivationStatus?.has_request && deactivationStatus?.status === 'PENDING' ? (
                            <div className="deactivation-pending">
                              <p>⚠️ Your deactivation request is pending review.</p>
                              <p className="reason-text"><strong>Reason:</strong> {deactivationStatus.reason}</p>
                              <button className="cancel-btn" onClick={handleCancelDeactivation}>Cancel Request</button>
                            </div>
                          ) : deactivationStatus?.status === 'REJECTED' ? (
                            <div className="deactivation-rejected">
                              <p>❌ Your deactivation request was rejected.</p>
                              {deactivationStatus.admin_notes && (
                                <p className="admin-notes"><strong>Admin Notes:</strong> {deactivationStatus.admin_notes}</p>
                              )}
                              <button className="request-deactivation-btn" onClick={() => setShowDeactivationModal(true)}>Request Again</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <h4 style={{ fontSize: '14px', color: 'var(--proto-white)' }}>Deactivate Account</h4>
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Temporarily disable your profile and sessions.</p>
                              </div>
                              <button className="request-deactivation-btn" onClick={() => setShowDeactivationModal(true)}>
                                Request Deactivation
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {settingsTab === 'bank-details' && (
                    <div className="pop-fade-up">
                      <BankDetails />
                    </div>
                  )}

                  {settingsTab === 'preferences' && (
                    <div className="pop-fade-up">
                      <div className="pop-glass-card">
                        <div className="card-header"><h3>Engagement Preferences</h3></div>
                        <div className="pop-settings-block">
                          <label className="pop-block-label">Delivery Modes</label>
                          <div className="pop-checkbox-list-row">
                            {['Remote', 'In-Person', 'Hybrid'].map(m => (
                              <label key={m} className={`pop-selection-chip ${availabilityMode.includes(m) ? 'active' : ''}`}>
                                <input type="checkbox" hidden checked={availabilityMode.includes(m)} onChange={() => toggleSelection(availabilityMode, setAvailabilityMode, m)} /> {m}
                              </label>
                            ))}
                          </div>
                        </div>


                        <div className="pop-settings-block" style={{ marginTop: '32px' }}>
                          <label className="pop-block-label">Engagement Categories</label>
                          <div className="pop-checkbox-list-row">
                            {['Visiting', 'Adjunct', 'Fractional', 'Modular'].map(cat => (
                              <label key={cat} className={`pop-selection-chip ${engagementCategories.includes(cat) ? 'active' : ''}`}>
                                <input type="checkbox" hidden checked={engagementCategories.includes(cat)} onChange={() => toggleSelection(engagementCategories, setEngagementCategories, cat)} /> {cat}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* GLOBAL AVAILABILITY RULES */}
                        <div className="pop-settings-block" style={{ marginTop: '32px' }}>
                          <label className="pop-block-label">Global Availability Rules</label>
                          <p className="pop-field-hint" style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                            These settings apply to <strong>ALL</strong> your engagements (Visiting, Adjunct, Modular, etc).
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div className="pop-field-wrapper">
                              <label>Global Weekly Capacity (Hours)</label>
                              <input
                                type="number"
                                value={weeklyQuotaHours}
                                onChange={e => setWeeklyQuotaHours(e.target.value)}
                                placeholder="e.g. 10"
                                style={{ width: '100%' }}
                              />
                              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                Total hours you can commit per week across all engagements.
                              </p>
                            </div>
                            <div className="pop-field-wrapper">
                              <label>Availability Window</label>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Start Date</span>
                                  <input
                                    type="date"
                                    value={windowStartDate}
                                    min={new Date().toISOString().split('T')[0]}
                                    onChange={e => setWindowStartDate(e.target.value)}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>End Date</span>
                                  <input
                                    type="date"
                                    value={windowEndDate}
                                    min={windowStartDate || new Date().toISOString().split('T')[0]}
                                    onChange={e => setWindowEndDate(e.target.value)}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              </div>
                              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                You will not be matched for opportunities outside these dates.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* VISITING SETTINGS */}
                        {engagementCategories.includes('Visiting') && (
                          <div className="pop-settings-block" style={{ marginTop: '20px', background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#15803d' }}>Visiting (Ad-Hoc) Mode</h4>
                            <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                              As a Visiting Professor, you select specific dates and times for your sessions (e.g., Nov 12th, 10 AM).
                              <br />
                              <strong>Action:</strong> Go to the <button className="pop-link-btn" onClick={() => setActiveTab('schedule')}>Schedule Tab</button> and click on specific dates to add "One-time" slots.
                            </p>
                          </div>
                        )}

                        {/* ADJUNCT SETTINGS */}
                        {engagementCategories.includes('Adjunct') && (
                          <div className="pop-settings-block" style={{ marginTop: '20px', background: '#fff7ed', padding: '16px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#c2410c' }}>Adjunct (Recurring) Mode</h4>
                            <p style={{ fontSize: '13px', color: '#9a3412', margin: 0 }}>
                              As an Adjunct Professor, you maintain a consistent weekly schedule (e.g., Every Mon & Wed).
                              <br />
                              <strong>Action:</strong> Go to the <button className="pop-link-btn" onClick={() => setActiveTab('schedule')}>Schedule Tab</button> and select "Smart Schedule" or click on any day to add "Weekly" slots.
                            </p>
                          </div>
                        )}

                        {/* FRACTIONAL SETTINGS */}
                        {engagementCategories.includes('Fractional') && (
                          <div className="pop-settings-block" style={{ marginTop: '20px', background: '#f0f9ff', padding: '16px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#0369a1' }}>Fractional Engagement</h4>
                            <p style={{ fontSize: '13px', color: '#0c4a6e', margin: 0 }}>
                              Matches will be based on your <strong>Global Weekly Capacity</strong> set above.
                            </p>
                          </div>
                        )}

                        {/* MODULAR SETTINGS */}
                        {engagementCategories.includes('Modular') && (
                          <div className="pop-settings-block" style={{ marginTop: '20px', background: '#fdf4ff', padding: '16px', borderRadius: '8px', border: '1px solid #e879f9' }}>
                            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#701a75' }}>Modular Engagement</h4>
                            <p style={{ fontSize: '13px', color: '#86198f', margin: 0 }}>
                              Matches will be restricted to your <strong>Global Availability Window</strong> set above.
                            </p>
                          </div>
                        )}

                        {/* Preferred Locations (New Section) - Only if In-Person or Hybrid */}
                        {(availabilityMode.includes('In-Person') || availabilityMode.includes('Hybrid')) && (
                          <div className="pop-settings-block" style={{ marginTop: '32px' }}>
                            <label className="pop-block-label">Preferred Locations (Max 3)</label>
                            <p className="pop-field-hint" style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                              Add up to 3 cities you are willing to travel to. Your home city is automatically included in matches.
                            </p>

                            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                              <input
                                placeholder="Add a city (e.g. Mumbai)"
                                id="new-loc-input"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '14px', width: '250px' }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = e.target.value.trim();
                                    if (val && preferredLocations.length < 3 && !preferredLocations.includes(val)) {
                                      setPreferredLocations([...preferredLocations, val]);
                                      e.target.value = '';
                                    }
                                  }
                                }}
                              />
                              <button
                                className="pop-btn-secondary"
                                type="button"
                                onClick={() => {
                                  const el = document.getElementById('new-loc-input');
                                  const val = el.value.trim();
                                  if (val && preferredLocations.length < 3 && !preferredLocations.includes(val)) {
                                    setPreferredLocations([...preferredLocations, val]);
                                    el.value = '';
                                  }
                                }}>Add</button>
                            </div>

                            <div className="pop-checkbox-list-row">
                              {preferredLocations.map(loc => (
                                <span key={loc} className="pop-selection-chip active" style={{ cursor: 'default', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {loc}
                                  <X
                                    size={14}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setPreferredLocations(preferredLocations.filter(l => l !== loc))}
                                  />
                                </span>
                              ))}
                              {preferredLocations.length === 0 && <span style={{ fontSize: '13px', color: 'var(--text-faint)', fontStyle: 'italic' }}>No locations added.</span>}
                            </div>
                            {preferredLocations.length >= 3 && <p style={{ fontSize: '12px', color: '#f59e0b', marginTop: '6px' }}>Maximum 3 locations allowed.</p>}
                          </div>
                        )}
                      </div>

                      <div className="pop-glass-card" style={{ marginTop: '24px' }}>
                        <div className="card-header">
                          <h3>Pricing Configuration</h3>
                          <p className="pop-header-sub">Set your rates for Student & University engagements</p>
                        </div>

                        {/* Student Pricing */}
                        <div className="pop-settings-block">
                          <h4 className="settings-mini-header">Student Mentoring & Sessions</h4>
                          <div className="pop-grid-2">
                            <div className="pop-field-wrapper">
                              <label>Individual Session Price (₹)</label>
                              <div style={{ position: 'relative' }}>
                                <input type="number" min="0" style={{ paddingLeft: '24px' }} value={individualPrice} onChange={e => setIndividualPrice(Math.max(0, parseInt(e.target.value) || 0))} placeholder="500" />
                              </div>
                              <span className="file-hint">Base price for 1-on-1 sessions</span>
                            </div>
                            <div className="pop-field-wrapper">
                              <label>Group Session Price (₹ per student)</label>
                              <div style={{ position: 'relative' }}>
                                <input type="number" min="0" style={{ paddingLeft: '24px' }} value={groupSessionPrice} onChange={e => setGroupSessionPrice(Math.max(0, parseInt(e.target.value) || 0))} placeholder="800" />
                              </div>
                              <span className="file-hint">Each student in a group session pays this amount</span>
                            </div>
                          </div>
                          <div className="pop-grid-2" style={{ marginTop: '16px' }}>
                            <div className="pop-field-wrapper">
                              <label>Minimum Group Size</label>
                              <input type="number" min="1" value={minGroupSize} onChange={e => setMinGroupSize(Math.max(1, parseInt(e.target.value) || 1))} placeholder="e.g. 3" />
                              <span className="file-hint">Required participants for group session</span>
                            </div>
                          </div>
                          <div className="pricing-preview-box">
                            <div className="preview-row">
                              <span>1-on-1 Session</span>
                              <strong>₹{individualPrice} / student</strong>
                            </div>
                            <div className="preview-row">
                              <span>Group Session (Min {minGroupSize})</span>
                              <strong>₹{groupSessionPrice} / student</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {settingsTab === 'documents' && (
                    <div className="pop-fade-up">
                      <div className="pop-glass-card">
                        <div className="card-header">
                          <h3>Vetting Status & Documents</h3>
                        </div>
                        <div id="profile-completion-card" className="completion-section">
                          <div className="completion-header">
                            <span>Profile Completion</span>
                            <span className={profileCompletion === 100 ? 'complete' : 'incomplete'}> {profileCompletion}%</span>
                          </div>
                          <div className="progress-bar-bg">
                            <div className="progress-bar-fill" style={{ width: `${profileCompletion}%`, backgroundColor: profileCompletion === 100 ? '#22c55e' : '#f59e0b' }} />
                          </div>
                          <div className="application-status-container" style={{ marginTop: '20px' }}>
                            <div className={`application-summary-pill ${applicationStatus?.status?.toLowerCase() || 'pending'}`}>
                              {applicationStatus?.status === 'APPROVED' ? <CheckCircle size={18} /> : <Clock size={18} />}
                              <span>Vetting Status: {applicationStatus?.status === 'APPROVED' ? (isVisible ? 'FULLY APPROVED' : 'PROFILE PRE-APPROVED') : (applicationStatus?.status?.replace('_', ' ') || 'NOT SUBMITTED')}</span>
                            </div>
                            <div className={`application-summary-pill ${profile.kyc_status?.toLowerCase() || 'pending'}`}>
                              {profile.kyc_status === 'ACCEPTED' ? <ShieldCheck size={18} /> : <Clock size={18} />}
                              <span>KYC Status: {profile.kyc_status ? profile.kyc_status.replace('_', ' ') : 'PENDING'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="documents-upload-grid" style={{ marginTop: '30px' }}>
                          {[
                            { type: 'cv_resume', label: 'CV/Resume', icon: FileText },
                            { type: 'degree_certificates', label: 'Degree Certificates', icon: FileCheck },
                            { type: 'pan_card', label: 'PAN Card (KYC)', icon: FileText },
                            { type: 'aadhaar_card', label: 'Aadhaar Card (KYC)', icon: FileText }
                          ].map(({ type, label, icon: Icon }) => (
                            <div key={type} className={`document-item ${documentStatus[type] ? 'uploaded' : 'pending'}`}>
                              <div className="doc-info">
                                <Icon size={20} className={documentStatus[type] ? 'icon-success' : 'icon-pending'} />
                                <div>
                                  <div className="doc-label">{label} *</div>
                                  {documentStatus[type] ? (
                                    <div className="doc-status-text">Uploaded ✓</div>
                                  ) : (
                                    <div className="doc-status-text">Required</div>
                                  )}
                                  {['pan_card', 'aadhaar_card'].includes(type) && documentStatus[type] && profile.kyc_status === 'REJECTED' && (
                                    <div style={{ color: '#ef4444', fontSize: '10px', fontWeight: 'bold' }}>REJECTED - Please Replace</div>
                                  )}
                                </div>
                              </div>
                              <div className="doc-actions">
                                {documentStatus[type] && (
                                  <button className="pop-btn-secondary small" onClick={() => handleDocumentDelete(type)}>Delete</button>
                                )}
                                <label className="upload-label">
                                  <input type="file" style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} accept=".pdf" onChange={e => { if (e.target.files[0]) handleDocumentUpload(type, e.target.files[0]); e.target.value = null; }} disabled={uploadingDoc === type} />
                                  <span className={`pop-btn-primary small ${uploadingDoc === type ? 'loading' : ''}`}>
                                    {uploadingDoc === type ? '...' : documentStatus[type] ? 'Replace' : 'Upload'}
                                  </span>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>



                        <div className="vetting-action-footer">
                          {!applicationStatus?.has_application ? (
                            <>
                              <p className="settings-page-subtitle" style={{ marginBottom: '16px' }}>Once your profile is complete and all required documents are uploaded, submit your application for vetting.</p>
                              <button
                                className={`pop-btn-primary full-width vetting-btn ${canSubmitForVetting ? 'ready' : 'disabled'}`}
                                onClick={handleSubmitForVetting}
                                disabled={!canSubmitForVetting || submittingApplication}
                              >
                                {submittingApplication ? 'Processing...' : canSubmitForVetting ? <><Upload size={18} /> Submit </> : <><showToastTriangle size={18} /> Complete Profile to Submit</>}
                              </button>
                            </>
                          ) : applicationStatus?.status === 'DECLINED' ? (
                            <div className="decline-notice">
                              <div className="decline-header"><span className="decline-icon">⚠️</span><h3>Application Declined</h3></div>
                              <div className="decline-reason"><strong>Reason:</strong><p>{applicationStatus.decline_reason || 'No reason provided'}</p></div>
                              <button className="pop-btn-primary full-width vetting-btn ready" onClick={handleSubmitForVetting} disabled={!canSubmitForVetting || submittingApplication}>
                                {submittingApplication ? 'Processing...' : <><Upload size={18} /> Resubmit for Vetting</>}
                              </button>
                            </div>
                          ) : applicationStatus?.status === 'APPROVED' ? null : (
                            <div className="application-status-pill">
                              <Clock size={18} />
                              <span>Status: Vetting in Progress</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          }
        </div>
      </main>

      {showDeactivationModal && (
        <div className="pop-modal-overlay" onClick={() => setShowDeactivationModal(false)}>
          <div className="pop-glass-card pop-fade-up" style={{ maxWidth: '480px', width: '100%', margin: 'auto', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="card-header" style={{ borderBottom: 'none', paddingBottom: '0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Request Account Deactivation</h3>
                </div>
              </div>
            </div>
            
            <div style={{ padding: '0 8px' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}>
                We're sorry to see you go. Please tell us why you want to deactivate your account:
              </p>
              
              <div className="pop-field-wrapper" style={{ width: '100%' }}>
                <textarea
                  value={deactivationReason}
                  onChange={(e) => setDeactivationReason(e.target.value)}
                  placeholder="Enter your reason here..."
                  rows={4}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px', padding: '0 8px' }}>
              <button className="pop-btn-secondary" onClick={() => setShowDeactivationModal(false)}>
                Cancel
              </button>
              <button 
                className="pop-btn-primary" 
                style={{ background: '#ef4444', borderColor: '#ef4444', color: '#fff' }}
                onClick={handleRequestDeactivation}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {
        uniAcceptModal.open && (
          <div className="pop-modal-overlay" onClick={() => setUniAcceptModal({ ...uniAcceptModal, open: false })}>
            <div className="pop-glass-card pop-fade-up" style={{ maxWidth: '550px', width: '100%', margin: 'auto', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h3 style={{ margin: 0 }}>Accept University Engagement</h3>
                <button className="pop-close-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setUniAcceptModal({ ...uniAcceptModal, open: false })}><XCircle size={22} /></button>
              </div>
              <div className="pop-profile-form-vertical" style={{ marginTop: '20px', overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                <div className="pop-field-wrapper">
                  <label>Engagement Title</label>
                  <input
                    value={uniAcceptModal.request?.requirement_title || ''}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151', fontWeight: '600' }}
                  />
                </div>
                <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                  <label>University</label>
                  <input
                    value={uniAcceptModal.request?.university_name || ''}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151' }}
                  />
                </div>
                <div className="pop-grid-2" style={{ marginTop: '16px' }}>
                  <div className="pop-field-wrapper">
                    <label>Date</label>
                    <input
                      value={uniAcceptModal.request?.event_date ? new Date(uniAcceptModal.request.event_date).toLocaleDateString('en-GB') : ''}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151' }}
                    />
                  </div>
                  <div className="pop-field-wrapper">
                    <label>Time</label>
                    <input
                      value={uniAcceptModal.request?.start_time || 'TBD'}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151' }}
                    />
                  </div>
                </div>
                <div className="pop-grid-2" style={{ marginTop: '16px' }}>
                  <div className="pop-field-wrapper">
                    <label>Duration</label>
                    <input
                      value={`${uniAcceptModal.request?.duration_value || 1} ${uniAcceptModal.request?.duration_unit || 'Hours'}`}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151' }}
                    />
                  </div>
                  <div className="pop-field-wrapper">
                    <label>Delivery Mode</label>
                    <input
                      value={uniAcceptModal.request?.delivery_mode || 'Remote'}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151' }}
                    />
                  </div>
                </div>

                {(uniAcceptModal.request?.end_date || uniAcceptModal.request?.end_time || (uniAcceptModal.request?.days_of_week && uniAcceptModal.request?.days_of_week.length > 0)) && (
                  <div className="pop-grid-2" style={{ marginTop: '16px' }}>
                    <div className="pop-field-wrapper">
                      <label>{uniAcceptModal.request?.engagement_type === 'ADJUNCT' ? 'Recurring Days' : 'End Date'}</label>
                      <div style={{ padding: '8px 12px', background: 'var(--engine-black)', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '14px', color: 'var(--proto-white)' }}>
                        {uniAcceptModal.request?.engagement_type === 'ADJUNCT' && uniAcceptModal.request?.days_of_week
                          ? uniAcceptModal.request.days_of_week.join(', ')
                          : uniAcceptModal.request?.end_date ? new Date(uniAcceptModal.request.end_date).toLocaleDateString('en-GB') : 'N/A'}
                      </div>
                    </div>
                    {uniAcceptModal.request?.end_time && (
                      <div className="pop-field-wrapper">
                        <label>End Time</label>
                        <input
                          value={uniAcceptModal.request?.end_time || ''}
                          disabled
                          style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151' }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {uniAcceptModal.request?.engagement_type === 'FRACTIONAL' && uniAcceptModal.request?.weekly_hours && (
                  <div className="pop-field-wrapper" style={{ marginTop: '16px' }}>
                    <label>Weekly Quota Hours</label>
                    <input
                      value={`${uniAcceptModal.request.weekly_hours} Hours per Week`}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#374151', fontWeight: '600' }}
                    />
                  </div>
                )}

                {/* Requirement Budget Info */}
                <div style={{ marginTop: '16px', padding: '12px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#166534', fontWeight: '500' }}>{uniAcceptModal.request?.budget ? "Uni's Total Budget" : "Est. Total Budget"}</span>
                  <span style={{ fontSize: '16px', color: '#15803d', fontWeight: '700' }}>
                    ₹{(uniAcceptModal.request?.total_estimated_amount || uniAcceptModal.request?.budget || 0).toLocaleString()}
                  </span>
                </div>

                {/* Price Calculation Section */}
                <div style={{ marginTop: '24px', padding: '16px', background: 'linear-gradient(to right, #faf5ff, #f3e8ff)', borderRadius: '12px', border: '1px solid #e9d5ff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Price Calculation</h4>
                    <span style={{ fontSize: '10px', background: '#ede9fe', color: '#7c3aed', padding: '4px 8px', borderRadius: '6px', border: '1px solid #ddd6fe' }}>Auto-Calculated</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#7c3aed', marginBottom: '4px' }}>Basis of Calculation</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#5b21b6' }}>
                        {uniAcceptModal.request?.budget ? "University's Fixed Budget" : `₹${(universityHourlyRate > 0 ? universityHourlyRate : uniAcceptModal.request?.hourly_rate)} × ${uniAcceptModal.request?.total_estimated_hours || uniAcceptModal.request?.duration_value || 1} Hours`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#7c3aed', marginBottom: '4px' }}>Total Amount</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#5b21b6' }}>₹{uniAcceptModal.proposed_amount?.toLocaleString()}</div>
                    </div>
                  </div>
                  {universityHourlyRate === 0 && (
                    <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '8px', fontStyle: 'italic' }}>
                      {/* ⚠️ Please set your university hourly rate in Settings → University Pricing before accepting engagements. */}
                    </p>
                  )}
                </div>

                <button
                  className="pop-btn-primary full-width"
                  style={{ marginTop: '24px' }}
                  onClick={submitUniversityAcceptance}
                  disabled={!uniAcceptModal.proposed_amount || uniAcceptModal.proposed_amount <= 0}
                >
                  Confirm Acceptance
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        acceptModal.open && (
          <div className="pop-modal-overlay" onClick={() => setAcceptModal({ ...acceptModal, open: false })}>
            <div className="pop-glass-card pop-fade-up" style={{ maxWidth: '600px', width: '100%', margin: 'auto', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
              <div className="card-header">
                <h3>Accept & Scheule Session</h3>
                <button className="pop-close-btn" onClick={() => setAcceptModal({ ...acceptModal, open: false })}><XCircle size={22} /></button>
              </div>
              <div className="pop-profile-form-vertical" style={{ marginTop: '20px', overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                <div className="pop-field-wrapper">
                  <label>Session Title (View Only)</label>
                  <input
                    value={acceptModal.form.title}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                  />
                </div>
                <div className="pop-grid-2">
                  <div className="pop-field-wrapper">
                    <label>Date (View Only)</label>
                    <input
                      type="date"
                      value={acceptModal.form.event_date}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                    />
                  </div>
                  <div className="pop-field-wrapper">
                    <label>Time (View Only)</label>
                    <input
                      type="time"
                      value={acceptModal.form.start_time}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                    />
                  </div>
                </div>
                <div className="pop-grid-2">
                  <div className="pop-field-wrapper">
                    <label>Duration (View Only)</label>
                    <input
                      value={acceptModal.form.duration}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                    />
                  </div>
                  <div className="pop-field-wrapper">
                    <label>
                      Price (INR)
                      <span style={{ fontSize: '11px', color: '#6366f1', marginLeft: '8px' }}>
                        (Fixed based on student request)
                      </span>
                    </label>
                    <input
                      type="number"
                      value={acceptModal.form.proposed_amount}
                      disabled
                      style={{
                        backgroundColor: '#f3f4f6',
                        cursor: 'not-allowed',
                        color: '#6b7280',
                        fontWeight: '600'
                      }}
                    />
                    {acceptModal.request?.session_type === 'GROUP' && (
                      <p style={{
                        fontSize: '11px',
                        color: 'var(--text-muted)',
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        Based on student's request for {acceptModal.request?.group_size || 10} members
                      </p>
                    )}
                  </div>
                </div>
                <div className="pop-field-wrapper">
                  <label>Meeting Link (Required)</label>
                  <input
                    placeholder="https://meet.google.com/..."
                    value={acceptModal.form.meeting_link}
                    onChange={e => setAcceptModal({ ...acceptModal, form: { ...acceptModal.form, meeting_link: e.target.value } })}
                  />
                </div>
                <div className="pop-field-wrapper">
                  <label>Notes / Description</label>
                  <textarea
                    rows={3}
                    value={acceptModal.form.description}
                    onChange={e => setAcceptModal({ ...acceptModal, form: { ...acceptModal.form, description: e.target.value } })}
                  />
                </div>
                <button className="pop-btn-primary full-width" style={{ marginTop: '20px' }} onClick={submitAcceptance}>
                  Confirm Acceptance
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        smartModal.open && (
          <div className="pop-modal-overlay" onClick={() => setSmartModal({ ...smartModal, open: false })}>
            <div className="pop-glass-card pop-fade-up" style={{ maxWidth: '450px', width: '100%', margin: 'auto', padding: '24px', borderRadius: '16px' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ background: '#fef3c7', padding: '8px', borderRadius: '50%', display: 'flex' }}>
                    <Zap size={20} className="text-yellow" style={{ color: '#d97706' }} />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Smart Schedule</h3>
                </div>
                <button
                  className="pop-close-btn"
                  onClick={() => setSmartModal({ ...smartModal, open: false })}
                  style={{ padding: '8px', borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <XCircle size={20} />
                </button>
              </div>

              <div style={{ marginTop: '20px' }}>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5' }}>
                  Select the days and time range you are generally available. We will generate recurring weekly slots for you.
                </p>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--proto-white)', marginBottom: '10px' }}>Select Active Days</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                      const isSelected = smartModal.selectedDays.includes(day);
                      return (
                        <button
                          key={day}
                          onClick={() => {
                            const newDays = isSelected
                              ? smartModal.selectedDays.filter(d => d !== day)
                              : [...smartModal.selectedDays, day];
                            setSmartModal({ ...smartModal, selectedDays: newDays });
                          }}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '50px',
                            fontSize: '13px',
                            fontWeight: 500,
                            border: isSelected ? '1px solid #4f46e5' : '1px solid var(--border)',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'var(--primary)' : 'var(--engine-black)',
                            color: isSelected ? 'var(--engine-black)' : 'var(--text-muted)',
                            border: isSelected ? '1px solid var(--primary)' : '1px solid var(--border)',
                            boxShadow: isSelected ? '0 4px 6px -1px rgba(79, 70, 229, 0.2)' : 'none'
                          }}
                        >
                          {day.substring(0, 3)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pop-grid-2" style={{ gap: '16px' }}>
                  <div className="pop-field-wrapper">
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--proto-white)', marginBottom: '6px', display: 'block' }}>Start Time</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="time"
                        value={smartModal.startTime}
                        onChange={e => setSmartModal({ ...smartModal, startTime: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px', fontWeight: 500 }}
                      />
                    </div>
                  </div>
                  <div className="pop-field-wrapper">
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--proto-white)', marginBottom: '6px', display: 'block' }}>End Time</label>
                    <input
                      type="time"
                      value={smartModal.endTime}
                      onChange={e => setSmartModal({ ...smartModal, endTime: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px', fontWeight: 500 }}
                    />
                  </div>
                </div>

                <button
                  className="pop-btn-primary full-width"
                  style={{ marginTop: '28px', padding: '14px', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  onClick={handleSmartScheduleSubmit}
                >
                  <Zap size={18} /> Generate Schedule
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Change Password Modal */}
      {
        showChangePasswordModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'var(--graphite-dark)',
              borderRadius: '16px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{
                padding: '24px 28px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Lock size={20} style={{ color: 'white' }} />
                  </div>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>
                    Change Password
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'var(--border)'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  <X size={20} style={{ color: 'var(--text-muted)' }} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '28px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    marginBottom: '8px'
                  }}>
                    Current Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Enter your current password"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    marginBottom: '8px'
                  }}>
                    New Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    marginBottom: '8px'
                  }}>
                    Confirm New Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Re-enter new password"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowChangePasswordModal(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '2px solid var(--border)',
                      background: 'var(--graphite-dark)',
                      color: 'var(--text-muted)',
                      fontWeight: '600',
                      fontSize: '15px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'var(--border)';
                      e.target.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'var(--border)';
                      e.target.style.background = 'white';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    className="pop-btn-primary"
                    style={{
                      padding: '12px 32px',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Lock size={16} />
                    Update Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Confirmation Modal */}
      {
        confirmModal.show && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: 'var(--bg-card)',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '520px',
              width: '95%',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.45)',
              border: '1px solid var(--border)',
              animation: 'modalFadeUp 0.3s ease-out'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: confirmModal.type === 'recurring_slot' ? 'var(--primary-soft)' : 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '32px'
                }}>
                  {confirmModal.type === 'recurring_slot' ? '📅' : '⚠️'}
                </div>
                <h2 style={{
                  margin: '0 0 12px',
                  fontSize: '24px',
                  fontWeight: '800',
                  color: 'var(--proto-white)'
                }}>
                  {confirmModal.type === 'simple_confirm' && 'Confirm Action'}
                  {confirmModal.type === 'recurring_slot' && 'Create Availability Slot'}
                  {confirmModal.type === 'recurring_delete' && 'Delete Recurring Slot'}
                  {confirmModal.type === 'vetting_submit' && 'Submit for Vetting'}
                </h2>
                <p style={{
                  margin: 0,
                  color: 'var(--text-muted)',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-line'
                }}>
                  {confirmModal.type === 'simple_confirm' && confirmModal.data.message}
                  {confirmModal.type === 'recurring_slot' && 'Would you like to create a recurring weekly slot or a one-time slot for this specific date?'}
                  {confirmModal.type === 'recurring_delete' && 'Would you like to delete the entire recurring series or just block this specific date?'}
                  {confirmModal.type === 'vetting_submit' && confirmModal.data.message}
                </p>
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {confirmModal.type === 'simple_confirm' && (
                  <>
                    <button
                      onClick={() => setConfirmModal({ show: false, type: null, data: null, onConfirm: null })}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: '2px solid var(--border)',
                        background: 'var(--graphite-dark)',
                        color: 'var(--text-muted)',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'var(--border)';
                        e.target.style.background = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'var(--border)';
                        e.target.style.background = 'white';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmModal.onConfirm()}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#ef4444',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                    >
                      Confirm
                    </button>
                  </>
                )}
                {confirmModal.type === 'recurring_slot' && (
                  <>
                    <button
                      onClick={() => confirmModal.onConfirm(false)}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: '2px solid #3b82f6',
                        background: 'var(--graphite-dark)',
                        color: '#3b82f6',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#3b82f6';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#3b82f6';
                      }}
                    >
                      This Date Only
                    </button>
                    <button
                      onClick={() => confirmModal.onConfirm(true)}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#3b82f6',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                    >
                      Every Week
                    </button>
                  </>
                )}
                {confirmModal.type === 'recurring_delete' && (
                  <>
                    <button
                      onClick={() => setConfirmModal({ show: false, type: null, data: null, onConfirm: null })}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: '2px solid var(--border)',
                        background: 'var(--graphite-dark)',
                        color: 'var(--text-muted)',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'var(--border)';
                        e.target.style.background = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'var(--border)';
                        e.target.style.background = 'white';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmModal.onConfirm('ONLY')}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#f59e0b',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#d97706'}
                      onMouseLeave={(e) => e.target.style.background = '#f59e0b'}
                    >
                      Block This Date
                    </button>
                    <button
                      onClick={() => confirmModal.onConfirm('ALL')}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#ef4444',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                    >
                      Delete All
                    </button>
                  </>
                )}
                {confirmModal.type === 'vetting_submit' && (
                  <>
                    <button
                      onClick={() => setConfirmModal({ show: false, type: null, data: null, onConfirm: null })}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: '2px solid var(--border)',
                        background: 'var(--graphite-dark)',
                        color: 'var(--text-muted)',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'var(--border)';
                        e.target.style.background = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'var(--border)';
                        e.target.style.background = 'white';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmModal.onConfirm()}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#10b981',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#059669'}
                      onMouseLeave={(e) => e.target.style.background = '#10b981'}
                    >
                      Submit
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' : '#ef4444',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
              maxWidth: '500px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideIn 0.3s ease-out',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <span style={{ fontSize: '20px' }}>
              {toast.type === 'success' ? '\u2713' : '\u2715'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.8'}
            >

            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes modalSlideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Prompt Modal */}
      {
        promptModal.show && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: 'var(--graphite-dark)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'modalSlideIn 0.3s ease-out'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '32px'
                }}>
                  ✏️
                </div>
                <h2 style={{
                  margin: '0 0 8px',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1f2937'
                }}>
                  {promptModal.title}
                </h2>
                <p style={{
                  margin: 0,
                  color: '#6b7280',
                  fontSize: '15px',
                  lineHeight: '1.6'
                }}>
                  {promptModal.message}
                </p>
              </div>
              <input
                type="text"
                defaultValue={promptModal.defaultValue}
                id="prompt-modal-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = e.target.value;
                    promptModal.onSubmit(value);
                  }
                  if (e.key === 'Escape') {
                    setPromptModal({ show: false, title: '', message: '', defaultValue: '', onSubmit: null });
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  fontSize: '15px',
                  marginBottom: '24px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4318ff'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
              />
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => setPromptModal({ show: false, title: '', message: '', defaultValue: '', onSubmit: null })}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = 'white';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const value = document.getElementById('prompt-modal-input').value;
                    promptModal.onSubmit(value);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#4318ff',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#3311cc'}
                  onMouseLeave={(e) => e.target.style.background = '#4318ff'}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PopDashboard;
